import type { AdInstance, Ads, ECPMOpts } from './ads';
import analytics from './analytics';
import AnalyticsProperties from './analytics/AnalyticsProperties';
import * as events from './analytics/events';
import {
  attachFirstTapListener,
  entryDataToPayloadIdentifierObject,
  getNextGenerationProperties,
  getReferralChainLengths,
  getSessionMaturityRange,
  isSocialChannel,
} from './analytics/utils';
import type { Community } from './community';
import { getAnalyticsTimeProperties } from './dateUtils';
import { unwrapNestedError } from './errorUtils';
import globalTime from './globalTime';
import * as userAgent from '@play-co/user-agent';

import ABTests from './ABTests';
import {
  createResolvablePromise,
  retryWithTiming,
  TimedRetryError,
} from './asyncUtils';
import Friend from './Friend';
import type { ConfirmationOptions } from './askConfirmation';
import type { FriendPickerOptions } from './friendPicker';
import { NativeBridge } from './NativeBridge';
import PlatformStorage from './PlatformStorage';
import { Product } from './Product';
import { SignedPlayerInfo } from './SignedPlayerInfo';
import { Purchase } from './Purchase';
import {
  getCountryNameFromIsoCode,
  getIsoCodeFromCountryName,
} from './countryCodes';
import {
  PostbackManager,
  PostbackManagerEvents,
} from '@play-co/postback-manager';
import { duration } from './dateUtils';
import { localStorage } from '@play-co/browser-storage';
import { ExternalABTests, ExternalABTestsSource } from './ExternalABTests';
import type { LinePhase } from './line/LineConnectedGame';

const LS_KEY = 'devkit-fbinstant:MockFBInstant';
const PLAYER_DATA_PREFIX = 'playerData';

let sessionSequence = 0;
const featureSessionSequences: any = {};

class VideoAdMock implements FBInstant.AdInstance {
  _placementID: string;
  _loaded: boolean;
  _viewed: boolean;

  constructor(placementID: string) {
    this._placementID = placementID;
    this._loaded = false;
    this._viewed = false;
  }

  getPlacementID() {
    return this._placementID;
  }

  loadAsync() {
    if (this._viewed) return Promise.reject(new Error());
    this._loaded = true;
    return Promise.resolve();
  }

  showAsync() {
    if (!this._loaded || this._viewed) return Promise.reject(new Error());
    this._viewed = true;
    return Promise.resolve();
  }
}

function setLocalStorageData(key: string, value) {
  try {
    if (!value) {
      localStorage.removeItem(LS_KEY + ':' + key);
    } else {
      localStorage.setItem(LS_KEY + ':' + key, JSON.stringify(value));
    }
  } catch (e) {
    console.error('FBINSTANT: mock setDataAsync FAILED!', e);
  }
}

export function getLocalStorageData(key: string): unknown {
  const value = localStorage.getItem(LS_KEY + ':' + key);

  if (value) {
    return JSON.parse(value);
  }
}

export function getOrSetLocalStorageData(
  key: string,
  defaultValue?: any,
): unknown {
  try {
    const value = localStorage.getItem(LS_KEY + ':' + key);

    if (value) {
      return JSON.parse(value);
    }

    setLocalStorageData(key, defaultValue);
  } catch (e) {
    console.error('FBINSTANT: mock getDataAsync FAILED!', e);
  }

  return defaultValue;
}

export type ActiveFriendCounts = {
  playerFriendCount: number;
  activeFriendCount1D: number;
  activeFriendCount3D: number;
  activeFriendCount7D: number;
  activeFriendCount14D: number;
  activeFriendCount30D: number;
  activeFriendCount90D: number;
};

export type ChooseContextOptions = FBInstant.ContextOptions & {
  analytics: CommonAnalytics;

  /**
   * Options for the LINE and Snapchat platform friend picker UI dialogs.
   *
   * Options enabled for Snapchat platform if `useFriendPicker` is true.
   */
  uiOpts?: FriendPickerOptions;

  /**
   * Options for customizing LINK message sent with choose context
   *
   * Only enabled on the LINK platform.
   */
  messageOpts?: {
    image?: string;
    text?: string | LocalizableContent;
    cta?: string;
    data: CommonAnalytics;
  };

  /**
   * Set to `true` to use GCInstant's friend picker UI instead of the Snapchat native UI.
   *
   * Only enabled on the Snapchat platform.
   *
   * @defaultValue `undefined`
   */
  useFriendPicker?: boolean;
};

/**
 * Player cluster counts.
 *
 * A cluster consists of the current player, all of their playing friends and all of the friends' playing friends.
 *
 * - `clusterSize` is the number of unique players in cluster.
 * - `clusterConnections` is the sum of connections between players in cluster.
 * - `clusterConnectedness` is `clusterSize` divided by `clusterConnections`.
 */
export type ClusterCounts = {
  clusterSize1D: number;
  clusterSize3D: number;
  clusterSize7D: number;
  clusterSize14D: number;
  clusterSize30D: number;
  clusterSize90D: number;
  clusterConnections1D: number;
  clusterConnections3D: number;
  clusterConnections7D: number;
  clusterConnections14D: number;
  clusterConnections30D: number;
  clusterConnections90D: number;
  clusterConnectedness1D: number;
  clusterConnectedness3D: number;
  clusterConnectedness7D: number;
  clusterConnectedness14D: number;
  clusterConnectedness30D: number;
  clusterConnectedness90D: number;
};

export type CommonAnalytics = {
  [key: string]: any;

  /** Feature that sent the API call (e.g.: "honor", "mission") */
  feature: string;

  /**
   * $subFeature that sent the API call
   * This is expected to be prefixed by feature,
   * e.g. "honor_band", "mission_impossible".
   */
  $subFeature: string | null;
};

export type ContextPlayer = { id: string; name: string; photo: string };

export type CreateSquadAsyncOptions =
  | CreateSquadAsyncFBOptions
  | CreateSquadAsyncLineOptions;

export type CreateSquadAsyncFBOptions = {
  /** `feature`, `$subFeature` and any custom analytics the game wants to track. */
  analytics: CommonAnalytics;

  /**
   * Optional pre-fill for the squad name input in the creation dialog.
   * The player ultimately has control over the actual name.
   */
  name?: string;

  /**
   * Optional base64 encoded image that will be associated with the gaming
   * squad and will show up in the creation dialog.
   * It will be the profile icon for the Messenger thread.
   */
  image?: string;

  /** Optional localized term to use in place of 'Squad' in the dialog. */
  squadTerm?: string;
};

export type CreateSquadAsyncLineOptions = {
  /** `feature`, `$subFeature` and any custom analytics the game wants to track. */
  analytics: CommonAnalytics;

  /** Squad chat room name (1-50 characters). */
  name: string;

  /** Squad chat room description (1-50 characters). */
  description: string;
};

/** Entry times as millisecond timestamps per player */
export type EntryTimestamps = {
  [playerId: string]: {
    firstEntryAt?: number;
    lastAdEntryAt?: number;
    lastEntryAt: number;
    zeroEntryAt?: number;
  };
};
export type EntryTimestampsGetter = (
  playerIds: string[],
) => Promise<EntryTimestamps>;

export type FBinstantTournament = {
  getContextID(): string | number; // FB tournament context ID can be either stringified or numeric
  getEndTime(): number;

  getTitle(): string;

  /** JSON.stringified payload object */
  getPayload(): string;
};

/** Game configuration object. */
export type GameConfig = {
  /**
   * API key for the secondary ad server analytics project in Amplitude.
   *
   * Overwritten by the `AMPLITUDE_AD_SERVER` environment variable.
   */
  amplitudeAdServerKey?: string;

  /**
   * API key for the main analytics project in Amplitude.
   *
   * Overwritten by the `AMPLITUDE` environment variable.
   */
  amplitudeKey?: string;

  /**
   * Amplitude project time zone. Defaults to `UTC` if the value is undefined, invalid or not supported by the client browser.
   *
   * Accepts any [IANA time zone](https://en.wikipedia.org/wiki/List_of_tz_database_time_zones) such as `America/Los_Angeles`.
   *
   * Overwritten by the `AMPLITUDE_TIME_ZONE` environment variable.
   */
  amplitudeTimeZone?: string;

  /**
   * Application ID.
   *
   * On the LINE platform this must match the uppercase game ID from
   * https://developers.game.line.me, for example `LGTL`.
   *
   * For platforms other than LINE use an arbitrary string value that uniquely
   * identifies the game.
   */
  appID: string;

  /**
   * Facebook Pixel ID.
   *
   * Overwritten by the `FB_PIXEL_ID` environment variable.
   */
  fbPixelId?: string;

  /**
   * LINE environment to target. Defaults to `production`.
   *
   * Used for the [LINE Connected Game Auth API](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/LIFF-Connected-Game-System/API-Documment&pos=user-content-liff-app-authentication) calls.
   *
   * Overwritten by the `LINE_PHASE` environment variable.
   */
  linePhase?: LinePhase;

  /**
   * LINE CORS proxy URL. Defaults to `https://line-relay-1.ap-northeast-1.dev.gc-internal.net`.
   *
   * Overwritten by the `LINE_CORS_PROXY_URL` environment variable.
   */
  lineCORSProxyURL?: string;

  /**
   * LINE Game API endpoint. Defaults to `https://game-api.gcld-line.com`.
   *
   * Used for the [LINE OpenChat API](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/LINE-GAME-Social/LINE-OPENCHAT/LINE-OPENCHAT-API-Reference) calls.
   *
   * Overwritten by the `LINE_GAME_API_ENDPOINT` environment variable.
   */
  lineGameAPIEndpoint?: string;

  /**
   * LINE Graph API endpoint. Defaults to `https://lg-release-trigraph-8080.gcld-line.com`.
   *
   * Used for the [LINE Connected Game Graph API](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/LIFF-Connected-Game-System/API-Documment&pos=user-content-graph-api) calls.
   *
   * Overwritten by the `LINE_GRAPH_API_ENDPOINT` environment variable.
   */
  lineGraphAPIEndpoint?: string;

  /**
   * LINE Social Message API endpoint. Defaults to `https://lg-release-trigraphevent-8080.gcld-line.com`.
   *
   * Used for [user-to-user message and image upload calls](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/LINE-GAME-Social/Using-LINE-Social-Message-API).
   *
   * Overwritten by the `LINE_MESSAGE_API_ENDPOINT` environment variable.
   */
  lineMessageAPIEndpoint?: string;

  /**
   * LINE Purchase API endpoint. Defaults to `https://lg-elogn.line-apps.com`.
   *
   * Used for [sending purchase data to LINE](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/Data-Analytics/Purchase-API).
   *
   * Overwritten by the `LINE_PURCHASE_API_ENDPOINT` environment variable.
   */
  linePurchaseAPIEndpoint?: string;

  /**
   * LINE LIFF app ID, for example `0123456789-ABcd1efg`.
   *
   * Overwritten by the `LINE_LIFF_ID` environment variable.
   */
  lineLiffID?: string;

  /**
   * LINE Official Account ID, for example `@123abcde`. Determines the redirect
   * location in LINE chatbot subscription.
   *
   * Overwritten by the `LINE_OA_ID` environment variable.
   */
  lineOfficialAccountID?: string;

  /**
   * Set to `true` to enable extra logging and development-only features.
   * Defaults to `false`.
   *
   * **Do not run production games in development mode.**
   *
   * Overwritten by the `IS_DEVELOPMENT` environment variable (set to `'true'` or boolean `true`).
   */
  isDevelopment?: boolean;

  /**
   * Override default mock platform signature.
   *
   * Overwritten by the `FB_INSTANT_MOCK_SIGNATURE` environment variable.
   */
  mockSignature?: string;

  /**
   * Postback Manager Key. Issued by the marketing team for ad network postback integration.
   * When this is specified a postback event is automatically fired on the `sendEntryFinalAnalytics` method call.
   *
   * Overwritten by the `POSTBACK_MANAGER_KEY` environment variable.
   */
  postbackManagerKey?: string;

  shortName: string;
  usePolyfills?: boolean;

  /**
   * Game version.
   *
   * @example '1.0.3'
   */
  version: string;

  /**
   * Used to access ZoomAPI https://marketplace.zoom.us/docs/api-reference/zoom-api
   *
   * Overwritten by the `ZOOM_API_ENDPOINT` environment variable.
   */
  zoomAPIEndpoint?: string;

  /**
   * List of native APIs and events, that must be passed to zoomSdk.config to verify your application
   */
  zoomSDKConfig?: ZoomSDKConfig;

  /**
   * Game-specific user properties to track when in the main Zoom client.
   *
   * These user properties are only attached to events tracked from the main Zoom client.
   *
   * Overwritten by the `ZOOM_MAIN_CLIENT_USER_PROPS` environment variable.
   */
  zoomMainClientUserProperties?: string[];

  /**
   * Game-specific user properties to track when in the meeting Zoom client.
   *
   * These user properties are only attached to events tracked from the meeting Zoom client.
   *
   * Overwritten by the `ZOOM_MEETING_CLIENT_USER_PROPS` environment variable.
   */
  zoomMeetingClientUserProperties?: string[];
};

export type LocalizableContent = {
  /**
   * The default value of the string to use if the viewer's locale is not a key in the localizations object.
   */
  default: string;

  /**
   * Specifies what string to use for viewers in each locale. Keys are locale names in `xx_XX` format, for example `en_US`.
   */
  localizations: { [locale: string]: string };
};

export type ZoomSDKConfig = {
  [key: string]: any;
  /**
   * list of API/events you plan to use
   * Overwritten by the ZOOM_SDK_CAPABILITIES environment variable
   */
  capabilities?: string[];
  /** Object that takes width and height of your desired app window size upon pop-out */
  size?: {
    width: number;
    height: number;
  };
};

export type InviteAsyncOptions = {
  /**
   * Invite message call-to-action button text.
   *
   * Only enabled on the Facebook and LINE platforms.
   *
   * @defaultValue `Play`, localized on the Facebook platform.
   */
  cta?: string | LocalizableContent;

  /**
   * Payload attached to the invite.
   *
   * Game sessions launched from the invite entry point will be able to access the payload through `Platform.entryData`.
   */
  data: CommonAnalytics;

  /**
   * Friend picker UI dialog title.
   *
   * Only enabled on the Facebook platform. Use `uiOpts` instead on the LINE platform.
   *
   * @defaultValue A localized form of `Play with friends`.
   */
  dialogTitle?: string | FBInstant.LocalizableContent;

  /**
   * Set to `false` to hide the "message sent" notification on LINE platform.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `true`
   */
  displayNotification?: boolean;

  /**
   * A set of filters to apply to the friend picker UI dialog suggestions.
   *
   * Multiple filters may be applied but filters that cancel each other out result in an `INVALID_PARAM` error.
   * If no results are returned when the filters are applied, the results will be generated without the filters.
   * Be aware that the results are not guaranteed to be filtered.
   *
   * - `EXISTING_CONTEXT_ONLY`: Prefer to only include people who are in a context with the current player.
   * - `EXISTING_PLAYERS_ONLY`: Prefer to only include people who have played the game before.
   * - `NEW_CONTEXT_ONLY`: Prefer to only include people who are not in a context with the current player.
   * - `NEW_PLAYERS_ONLY`: Prefer to only include people who havenʼt played the game before.
   *
   * Only enabled on the Facebook platform. Use `uiOpts` to filter receivers on the LINE platform.
   *
   * @defaultValue `undefined`
   */
  filters?: (
    | 'EXISTING_CONTEXT_ONLY'
    | 'EXISTING_PLAYERS_ONLY'
    | 'NEW_CONTEXT_ONLY'
    | 'NEW_PLAYERS_ONLY'
  )[];

  /** A base64 encoded image to be shared. */
  image: string;

  /**
   * List of receiver player IDs. If defined, the friend picker UI is not
   * displayed and invites are sent to `receiverIDs` instead of selected
   * friends.
   *
   * Note that the friend selection confirmation UI is still shown unless
   * disabled with `uiOpts.confirmPopup`.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `undefined`
   */
  receiverIDs?: string[];

  /**
   * The set of sections included in the friend picker UI dialog.
   *
   * Each section can be assigned a maximum number of results to be returned (up to a maximum of 10).
   * If no max is included, a default max will be applied.
   *
   * Sections will be included in the order they are listed in the array.
   * The last section will include a larger maximum number of results, and if a `maxResults` is provided, it will be ignored.
   *
   * Only enabled on the Facebook platform.
   *
   * @defaultValue `undefined`
   */
  sections?: { sectionType: 'GROUPS' | 'USERS'; maxResults?: number }[];

  /**
   * Identifier for the game-side UI used to select share receivers. Use in
   * conjunction with `receiverIDs`.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `undefined`
   */
  selectionUI?: string;

  /**
   * LINE Social Message Template ID.
   *
   * LINE platform templates are configured in the [LINE Game Developer console](https://developers.game.line.me/).
   * See [the Social Message Templates documentation](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/understand/apps/services/GRAPH-EVENT/Message-Template-Manual)
   * for more.
   *
   * Only enabled on the LINE platform. Required property on the LINE platform.
   */
  template?: string;

  /** Invite message. */
  text: string | LocalizableContent;

  /**
   * Title to pass to the invite message.
   *
   * Only enabled on the Snapchat platform.
   */
  title?: string;

  /**
   * Options for the LINE and Snapchat platform friend picker UI dialog.
   *
   * Only enabled on the LINE platform.
   * Options enabled for Snapchat platform if `useFriendPicker` is true.
   */
  uiOpts?: FriendPickerOptions;
};

export type JoinSquadAsyncOptions =
  | JoinSquadAsyncFBOptions
  | JoinSquadAsyncLineOptions;

export type JoinSquadAsyncFBOptions = {
  /** `feature`, `$subFeature` and any custom analytics the game wants to track. */
  analytics: CommonAnalytics;

  /**
   * Optional localized term to use in place of 'Squad' in the dialog
   *
   * Only enabled on the Facebook platform.
   */
  squadTerm?: string;
};

export type JoinSquadAsyncLineOptions = {
  /** `feature`, `$subFeature` and any custom analytics the game wants to track. */
  analytics: CommonAnalytics;

  /**
   * Username displayed in the squad chat room (1-50 characters).
   *
   * The username cannot contain the user's LINE ID, ignoring whitespace and casing.
   * For example in case of a LINE ID `foobar`, display name `asdFoo Bar123` is invalid.
   *
   * Only enabled on the LINE platform.
   */
  displayName: string;
};

export type MediaContent =
  | { gif: { url: string } }
  | { video: { url: string } };

export type ShareAsyncOptions = {
  /**
   * Optional call-to-action button text.
   *
   * Only enabled on the Link platform.
   *
   * @defaultValue Default CTA string set by the Link platform.
   */
  cta?: string;

  /**
   * Payload attached to the share which every game session launched from the
   * share entry point will be able to access through `Platform.entryData`.
   */
  data: CommonAnalytics;

  /**
   * Set to `false` to hide the "message sent" notification on LINE platform.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `true`
   */
  displayNotification?: boolean;

  /**
   * A base64 encoded image to be shared.
   */
  image: string;

  /**
   * Optional properties to control the shareable image.
   *
   * Only enabled on the Snapchat platform.
   */
  imageProps?: {
    width?: number;
    height?: number;
    centerX?: number;
    centerY?: number;
    rotation?: number;
  };

  /**
   * Optional gif or video content.
   *
   * Only enabled on the Facebook platform.
   */
  media?: MediaContent;

  /**
   * List of receiver player IDs. If defined, the friend picker UI is not
   * displayed and shares are sent to `receiverIDs` instead of selected
   * friends.
   *
   * Note that the friend selection confirmation UI is still shown unless
   * disabled with `uiOpts.confirmPopup`.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `undefined`
   */
  receiverIDs?: string[];

  /**
   * Identifier for the game-side UI used to select share receivers. Use in
   * conjunction with `receiverIDs`.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `undefined`
   */
  selectionUI?: string;

  /**
   * List of share destinations.
   *
   * Only enabled on the Facebook platform, in private beta.
   *
   * @defaultValue `undefined`
   */
  shareDestination?: ShareDestination[];

  /**
   * Set to `true` to switch to the created context after the share succeeds.
   *
   * Note that on the Facebook platform the `shareAsync` call only rejects when
   * user dismisses the share dialog if `switchContext` is set to `true`.
   *
   * Only enabled on the Facebook platform.
   *
   * @defaultValue `false`
   */
  switchContext?: boolean;

  /**
   * LINE Social Message Template ID.
   *
   * LINE platform templates are configured in the [LINE Game Developer console](https://developers.game.line.me/).
   * See [the Social Message Templates documentation](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/understand/apps/services/GRAPH-EVENT/Message-Template-Manual)
   * for more.
   *
   * Only enabled on the LINE platform. Required property on the LINE platform.
   */
  template?: string;

  /**
   * A text message to be shared.
   */
  text: string;

  /**
   * Options for the LINE platform friend picker UI dialog.
   *
   * Only enabled on the LINE platform.
   */
  uiOpts?: FriendPickerOptions;
};

export type ShareDestination = 'COPY_LINK' | 'GROUP' | 'MESSENGER' | 'NEWSFEED';

export type ShareLinkAsyncOptions = {
  image: string;
  text: string;
  data: CommonAnalytics;
};

export type Tournament = {
  getContextID(): string;

  /** Unix timestamp (in seconds) of when tournament ends. */
  getEndTime(): number;

  getTitle(): string;
  getPayload(): Promise<AnalyticsProperties.EntryData>;
};

export type Squad = {
  /**
   * Get the unique ID associated with this squad
   */
  getID(): string;

  /**
   * Get the contextID associated with this squad and squad owner
   */
  getContextID(): string;

  /**
   * Get squad description.
   *
   * Only enabled on the LINE platform.
   */
  getDescription(): string;

  /**
   * The name of the squad
   */
  getName(): string;

  /**
   * The image URL associated with the squad thread
   *
   * Only enabled on the Facebook platform.
   */
  getImage(): string;

  /**
   * Join squad if not already a member and switch to squad context.
   *
   * @throws `UNAUTHORIZED` if user has not granted OpenChat permissions on LINE.
   * @throws `USER_INPUT` if user dismissed the UI dialog on Facebook.
   * @throws If the platform SDK call fails for any other reason.
   */
  joinSquadAsync(opts: JoinSquadAsyncOptions): Promise<void>;
};

export type UpdateAsyncOptions = {
  /** Optional call-to-action button text. */
  cta?: string | LocalizableContent;

  /**
   * Payload attached to the update.
   *
   * Game sessions launched from the update entry point will be able to access the payload through `Platform.entryData`.
   */
  data: CommonAnalytics;

  /**
   * Set to `false` to hide the "message sent" notification on the LINE platform.
   *
   * Only enabled on the LINE platform.
   *
   * @defaultValue `true`
   */
  displayNotification?: boolean;

  /** Data URL of a base64 encoded image. */
  image: string;

  /**
   * Optional gif or video content.
   *
   * Only enabled on the Facebook platform.
   */
  media?: MediaContent;

  /**
   * Update notification setting.
   *
   * Use `PUSH` only for updates that are high-signal and immediately actionable for the recipients.
   * Also note that push notification is not always guaranteed, depending on user setting and platform policies.
   *
   * Only enabled on the Facebook, Link and Viber platforms.
   *
   * @defaultValue `NO_PUSH`
   */
  notification?: 'PUSH' | 'NO_PUSH';

  /**
   * Update target user. If defined, an `UpdateReceiptSuccess` event is
   * sent to receiving user's analytics stream after a successful update.
   */
  receiver?: {
    id: string;
    userProperties?: { [key: string]: any };
  };

  /**
   * Update delivery strategy.
   *
   * - `IMMEDIATE`: Post update immediately.
   * - `LAST`: Post update when the game session ends. The most recent update sent using the `LAST` strategy will be the one sent.
   * - `IMMEDIATE_CLEAR`: Post update immediately and clear any other pending updates (such as those sent with the `LAST` strategy).
   *
   * The Facebook platform only supports `IMMEDIATE` and `LAST`.
   *
   * The LINE platform only supports `IMMEDIATE`.
   *
   * @defaultValue `IMMEDIATE`
   */
  strategy?: AnalyticsProperties.UpdateStrategy;

  /**
   * Update template ID.
   *
   * Facebook platform templates are defined in `fbapp-config.json`.
   * See the [Bundle Config documentation](https://developers.facebook.com/docs/games/instant-games/bundle-config)
   * for documentation about `fbapp-config.json`.
   *
   * LINE platform templates are configured in the [LINE Game Developer console](https://developers.game.line.me/).
   * See [the Social Message Templates documentation](https://developers.game.line.me/document/guides/detail?id=/Developer_Guide/understand/apps/services/GRAPH-EVENT/Message-Template-Manual)
   * for more.
   *
   * Snap platform templates are created in Puppy CMS and must be approved by Snap to be used.
   * See [Puppy Documentation on custom chat notifications](https://support.canvas.snapchat.com/hc/en-us/articles/4403235084820-Custom-Chat-Notifications).
   * See [Puppy Documentation on custom chat updates](https://support.canvas.snapchat.com/hc/en-us/articles/4403242944148-Custom-Chat-Messages).
   */
  template: string;

  /**
   * Run-time parameters that can be passed into a template.
   *
   * Only enabled on the Snap platform.
   * See [Puppy Documentation on custom chat notifications](https://support.canvas.snapchat.com/hc/en-us/articles/4403235084820-Custom-Chat-Notifications).
   * See [Puppy Documentation on custom chat updates](https://support.canvas.snapchat.com/hc/en-us/articles/4403242944148-Custom-Chat-Messages).
   */
  templateParams?: Record<string, string>;

  /** Update message. */
  text: string | LocalizableContent;

  /** Options for the LINE platform friend picker UI dialog. */
  uiOpts?: FriendPickerOptions;
};

export type UpdateAsyncResult = {
  /** Blob of data to attach to the update: `opts.data` supplemented with contextual properties. */
  payloadData: { [key: string]: any };

  /** Value returned from the underlying platform API. */
  returnValue: any;
};

export type DataEncoder = (payload: Record<string, any>) => Record<string, any>;
export type DataDecoder = (
  encodedPayload: Record<string, any>,
) => Promise<AnalyticsProperties.EntryData>;
export type DataCodec = {
  /**
   * Encoder for data sent to platform-specific API, to later be retrieved as a payload on game start.
   * More info in [Payload Encoding](https://docs.dev.gc-internal.net/gcinstant-replicant-extensions/index.html#payload-encoding).
   */
  encode: DataEncoder;
  /**
   * Decoder for payloads retrieved from platform-specific API (e.g. on game start).
   * More info in [Payload Encoding](https://docs.dev.gc-internal.net/gcinstant-replicant-extensions/index.html#payload-encoding).
   */
  decode: DataDecoder;
};

type TimedMethod =
  | 'initializeAsync'
  | 'getCatalogAsync'
  | 'getSignedPlayerInfoAsync'
  | 'startGameAsync'
  | 'loadStorage'
  | 'getFriendsAsync'
  | 'canSubscribeBotAsync'
  | 'canCreateShortcutAsync'
  | 'checkCanPlayerMatchAsync'
  | 'getEntryPointAsync'
  | 'getSquadAsync'
  | 'getTournamentAsync'
  | 'canSwitchNativeGameAsync';

const dayInMs = 1000 * 60 * 60 * 24;

export type CreateTournamentOptions = {
  /** Best score this session. */
  initialScore: number;
  /** Used to customize the in-game UI and tournament posts on Facebook in compliance with integrity rules. If undefined, Facebook will use the game's name */
  title?: string;
  /** Image size should be 680*340. If undefined, Facebook will use a default image for a tournament post */
  image?: string;
  /** Developer can retrieve this data from <Tournament>.getPayload(). This data is immutable. */
  data: CommonAnalytics;
};

export type ShareTournamentOptions = {
  /** Best score this session */
  score: number;
  /** Developer can retrieve this data from `FBInstant.getEntrypointData()`. */
  data: CommonAnalytics;
};

export type ProvisionProductAsyncCallback = (
  purchase: Purchase,
  product: Product,
  opts: {
    consumedAtLaunch: boolean;
  },
) => Promise<void>;

export const GAME_CONFIG_DEFAULTS: Partial<GameConfig> = {
  linePhase: 'release',
  lineCORSProxyURL: 'https://line-relay-1.ap-northeast-1.dev.gc-internal.net',
  lineGameAPIEndpoint: 'https://game-api.gcld-line.com',
  lineGraphAPIEndpoint: 'https://lg-release-trigraph-8080.gcld-line.com',
  lineMessageAPIEndpoint: 'https://lg-release-trigraphevent-8080.gcld-line.com',
  linePurchaseAPIEndpoint: 'https://lg-elogn.line-apps.com',
  isDevelopment: false,
};

export abstract class Platform {
  static ERROR_NO_PROVISION: string;

  /**
   * A/B tests manager, defined and ready to use after loading platform storage.
   *
   * @example
   * ```ts
   * await GCInstant.loadStorage();
   * GCInstant.abTests.initialize(config, generateHash);
   * const bucketId = GCInstant.abTests.getBucketID('testId');
   * ```
   */
  abTests?: ABTests;

  /**
   * Methods for loading and displaying in-game ads.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   *
   * Only implemented on the Facebook, Snapchat, and Viber platforms.
   */
  ads: Ads = {
    preloadInterstitialAdAsync: () => Promise.reject('Not implemented'),
    preloadRewardedVideoAdAsync: () => Promise.reject('Not implemented'),
    showPreloadedInterstitialAdAsync: () => Promise.reject('Not implemented'),
    showPreloadedRewardedVideoAdAsync: () => Promise.reject('Not implemented'),
  };

  appID: string;

  /**
   * Useable after `waitForEarlyEntryData()` or `waitForEntryAnalytics()` resolves. Before that it will always be `false`.
   *
   * On the LINE platform this is `false` is user has not granted the OpenChat permissions required for squads.
   *
   * Only enabled on the Facebook and LINE platforms.
   */
  canUseSquads = false;

  canChangeContext: boolean;
  canCreateShortcut: boolean;
  canPlayerMatch: boolean;

  /**
   * True if platform can show interstitial ads.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canShowInterstitialAds(): boolean {
    this.throwIfNotInitialized();
    return this._canShowInterstitialAds;
  }
  protected _canShowInterstitialAds = false;

  /**
   * True if platform can show rewarded video ads.
   *
   * @throws If called before `initializeAsync` resolves.
   */
  get canShowRewardedVideoAds(): boolean {
    this.throwIfNotInitialized();
    return this._canShowRewardedVideoAds;
  }
  protected _canShowRewardedVideoAds = false;

  canSubscribeToBot: boolean;

  catalog: Product[];

  /**
   * Interact with official game pages and groups.
   *
   * Only enabled on the Facebook platform.
   */
  community: Community = {
    canFollowOfficialPageAsync: () => Promise.reject('Not implemented'),
    canJoinOfficialGroupAsync: () => Promise.reject('Not implemented'),
    followOfficialPageAsync: () => Promise.reject('Not implemented'),
    joinOfficialGroupAsync: () => Promise.reject('Not implemented'),
  };

  contextID: string | null;
  contextSquad: Squad | null = null;
  contextTournament: Tournament | null = null;
  contextType: string;
  entryAnalyticsBundle: any;
  entryContextTournament: Tournament | null = null;
  entryData: AnalyticsProperties.EntryData | null = null;
  entryPointName: string | undefined;

  /** IDs of playing platform friends. See `Platform.friends` for usage. */
  friendIds: readonly string[] = [];

  /**
   * Platform friends who play the game.
   *
   * On the Facebook platform this is limited to friends who played within the last 90 days.
   * See `Platform.socialFriends` for accessing non-playing friends on the LINE platform.
   *
   * The value does not change during a session.
   *
   * @example Accessing friends
   * ```ts
   * await GCInstant.initializeAsync(config);
   * await GCInstant.waitForFriends(); // block until friends are resolved
   * GCInstant.friends;
   * ```
   */
  friends: Friend[];

  /** Playing platforms friends mapped by ID. See `Platform.friends` for usage. */
  friendsMap: { [id: string]: Friend };

  insideNativeIOS: boolean;
  isMobile: boolean;
  isSubscribedToBot = false;
  locale: string;
  nativeBridge: NativeBridge;
  osType:
    | 'IOS'
    | 'IOS_APP'
    | 'ANDROID'
    | 'ANDROID_APP'
    | 'WEB'
    | 'MOBILE_WEB'
    | 'DESKTOP'
    | undefined;
  paymentsAvailable: boolean;

  /**
   * All platform friends including non-playing ones. Only populated on the LINE platform.
   *
   * Use `Friend.isPlayer` to distinguish between playing and non-playing friends.
   */
  socialFriends: Friend[] = [];

  platformLocale: string;

  /**
   * Player's avatar id, accessible after `initializeAsync`.
   *
   * Avatar ID identifies player's 'bitmoji'
   *
   * Only defined on the Snapchat platform.
   */
  playerAvatarId?: string;

  playerID: string;
  playerName: string;
  /**
   * Player's email address, accessible after `initializeAsync`.
   *
   * Only defined on the Zoom platform.
   */
  playerEmail: string;
  playerPhoto: string;
  players: any[];
  playerSignature: string;
  regionCode: string | null;

  /**
   * Bottom inset in pixels.  A safe area inset allows you to calculate a safe view, which guarantees an area not already
   * covered by navigation bars, tab bars, tool bars, etc.
   *
   * Only defined on the Snapchat platform. Accessible after resolving `initializeAsync`.
   */
  safeAreaInsetBottom: number;

  /**
   * Top inset in pixels.  A safe area inset allows you to calculate a safe view, which guarantees an area not already
   * covered by navigation bars, tab bars, tool bars, etc.
   *
   * Only defined on the Snapchat platform. Accessible after resolving `initializeAsync`.
   */
  safeAreaInsetTop: number;

  storage: PlatformStorage;
  stsid: number;
  supportedAPIs: Partial<Record<keyof Platform, boolean>>;

  protected beforeContextSwitchCallback?: () => void;
  protected afterContextSwitchCallback?: () => void;
  private didCallStartGameAsync = false;
  private didCallSendEntryFinalAnalytics = false;
  private didTrackFirstButtonTap = false;
  private countryName: string | undefined;
  protected eCPM: ECPMOpts | undefined;
  private trackPinchSessionCount = 0;
  private trackPinchSessionCounts: Partial<{ [resource: string]: number }> = {};
  private externalABTests = new ExternalABTests({
    getInternalABTag: () =>
      this.abTests?.config ? this.abTests.getAbTag() : '',
    getInternalABTestIDs: () =>
      this.abTests?.config ? Object.keys(this.abTests?.config) : [],
  });

  protected exchangeRatesUSD: Partial<{ [currencyCode: string]: number }> = {};

  /**
   * Resolves after early platform entry sequence finishes, or immediately in
   * case early platform APIs are not available.
   */
  waitForEarlyEntryData(): Promise<void> {
    this.throwIfNotInitialized();

    return this.entryPromise || Promise.resolve();
  }

  protected entryPromise: Promise<void> | undefined;

  /** Operations to retry after game start in case client didn't support calling the API early */
  protected onGameStartRetries: (() => Promise<any>)[] = [];

  private readonly friendsAvailablePromise = createResolvablePromise();

  /** Resolves after initial platform friends load finishes and `friends` is populated.
   *
   *  @throws if platform friends load fails.
   */
  waitForFriends = (): Promise<void> => this.friendsAvailablePromise.promise;
  protected resolveFriendsAvailable = this.friendsAvailablePromise.resolve;
  protected rejectFriendsAvailable = this.friendsAvailablePromise.reject;

  /** Entry sequence async API call timings, in seconds since window load. */
  private timeTo = {} as { [methodName in TimedMethod]: number };

  /** Set to true if platform data APIs are accessible right after `initializeAsync` */
  protected wasEntryDataReadyEarly = false;

  /** In PlatformFacebook, set to fallback if entry event payload comes from tournament entry context payload due to no entry data available.  Otherwise, we'll mark it as direct */
  protected tournamentPayloadMode: 'direct' | 'fallback';

  private entryTimestampsGetter: EntryTimestampsGetter | undefined;
  private previousEntryTimestamp: number | null = null;
  private readonly _entryAnalyticsPromise: Promise<void>;
  private _resolveEntryAnalytics: {
    resolve: () => void;
    reject: (err: any) => void;
  };
  _entryDataPromise: any;
  _gameConfig: GameConfig;
  _gameStarted: boolean;
  _initialization: null | Promise<void>;
  _initialized: boolean;
  _instance: any;
  _isPaymentsReady: boolean;
  private _loadStoragePromise?: Promise<void>;
  _matchingEnabled: boolean;
  _onPause: (() => void) | null;
  _onPaymentsReady: any;
  _onProvisionProductAsync: ProvisionProductAsyncCallback | null;
  _resolveEntryData: { resolve: () => void; reject: (err: unknown) => void };
  _sessionStartTime: number;
  _supportedAPIs: any[];

  protected dataCodec: DataCodec = {
    encode: (data) => data,
    decode: (data) => Promise.resolve(data as AnalyticsProperties.EntryData),
  };

  constructor(
    public platformID?:
      | 'fb'
      | 'line'
      | 'line-guest'
      | 'link'
      | 'mock'
      | 'snapchat'
      | 'viber'
      | 'web'
      | 'zoom',
  ) {
    this.playerID = '';
    this.playerName = '';
    this.playerPhoto = '';
    this.playerSignature = '';
    this.friends = [];
    this.friendsMap = {};
    this.players = [];
    this.catalog = [];
    this.contextID = null;
    this.contextType = 'SOLO';
    this.platformLocale = 'en';
    this.locale = 'en';
    this.regionCode = null;
    this.isMobile = false; // TODO
    this.paymentsAvailable = false;
    this.insideNativeIOS = false;

    this.canSubscribeToBot = false;

    this._initialization = null;
    this._initialized = false;
    this._gameStarted = false;
    this._onPause = null;
    this._onPaymentsReady = null;
    this._isPaymentsReady = false;
    this._onProvisionProductAsync = null;
    this._matchingEnabled = false;
    this._supportedAPIs = []; // TODO
    this._sessionStartTime = Date.now();

    // TODO: should be env variable (to strip out platform specific game code)
    this.canChangeContext = false;
    this.canCreateShortcut = false;

    // Checks if the current player is eligible for the matchPlayerAsync API.
    // See: checkCanPlayerMatchAsync( ) in https://developers.facebook.com/docs/games/instant-games/sdk/fbinstant6.3
    this.canPlayerMatch = false;

    this.supportedAPIs = {};

    this._gameConfig = null;

    this.entryAnalyticsBundle = {};

    this._resolveEntryData;
    this._entryDataPromise = new Promise<void>((resolve, reject) => {
      this._resolveEntryData = { resolve, reject };
    });

    this.storage = new PlatformStorage(this);

    this.stsid = Date.now();

    this._entryAnalyticsPromise = new Promise<void>((resolve, reject) => {
      this._resolveEntryAnalytics = { resolve, reject };
    });
  }

  /**
   * Initializes the SDK library. This should be called before any other SDK functions.
   *
   * This method can be called multiple times: subsequent calls return the initial call's
   * cached return value without re-initializing.
   *
   * @param config Game configuration object, required only on the initial call.
   * @returns A promise that resolves when the SDK is ready to use.
   */
  abstract initializeAsync(config?: GameConfig): Promise<void>;

  protected readConfiguration(config: GameConfig): void {
    if (config.appID && typeof config.appID === 'string') {
      this.appID = config.appID;
    }

    this._gameConfig = { ...GAME_CONFIG_DEFAULTS, ...config };

    // Environment variables, if defined, take precedence over `config` parameter:
    try {
      if (process.env.APP_ID || process.env.FB_APP_ID) {
        this.appID = process.env.APP_ID || process.env.FB_APP_ID;
      }
      if (process.env.AMPLITUDE) {
        this._gameConfig.amplitudeKey = process.env.AMPLITUDE;
      }
      if (process.env.AMPLITUDE_AD_SERVER) {
        this._gameConfig.amplitudeAdServerKey = process.env.AMPLITUDE_AD_SERVER;
      }
      if (process.env.AMPLITUDE_TIME_ZONE) {
        this._gameConfig.amplitudeTimeZone = process.env.AMPLITUDE_TIME_ZONE;
      }
      if (process.env.FB_PIXEL_ID) {
        this._gameConfig.fbPixelId = process.env.FB_PIXEL_ID;
      }
      if (process.env.FB_INSTANT_MOCK_SIGNATURE) {
        this._gameConfig.mockSignature = process.env.FB_INSTANT_MOCK_SIGNATURE;
      }
      if (
        process.env.IS_DEVELOPMENT === 'true' ||
        (typeof process.env.IS_DEVELOPMENT === 'boolean' &&
          process.env.IS_DEVELOPMENT === true)
      ) {
        this._gameConfig.isDevelopment = true;
      }
      if (process.env.LINE_PHASE) {
        this._gameConfig.linePhase = (
          {
            sandbox: 'sandbox',
            staging: 'staging',
            release: 'release',
          } as Record<string, LinePhase>
        )[process.env.LINE_PHASE.toLowerCase()];

        if (!this._gameConfig.linePhase) {
          throw new Error(
            'Unexpected value for LINE_PHASE: ' +
              JSON.stringify(process.env.LINE_PHASE),
          );
        }
      }
      if (process.env.LINE_CORS_PROXY_URL) {
        this._gameConfig.lineCORSProxyURL = process.env.LINE_CORS_PROXY_URL;
      }
      if (process.env.LINE_GAME_API_ENDPOINT) {
        this._gameConfig.lineGameAPIEndpoint =
          process.env.LINE_GAME_API_ENDPOINT;
      }
      if (process.env.LINE_LIFF_ID) {
        this._gameConfig.lineLiffID = process.env.LINE_LIFF_ID;
      }
      if (process.env.LINE_GRAPH_API_ENDPOINT) {
        this._gameConfig.lineGraphAPIEndpoint =
          process.env.LINE_GRAPH_API_ENDPOINT;
      }
      if (process.env.LINE_MESSAGE_API_ENDPOINT) {
        this._gameConfig.lineMessageAPIEndpoint =
          process.env.LINE_MESSAGE_API_ENDPOINT;
      }
      if (process.env.LINE_PURCHASE_API_ENDPOINT) {
        this._gameConfig.linePurchaseAPIEndpoint =
          process.env.LINE_PURCHASE_API_ENDPOINT;
      }
      if (process.env.LINE_OA_ID) {
        this._gameConfig.lineOfficialAccountID = process.env.LINE_OA_ID;
      }
      if (process.env.POSTBACK_MANAGER_KEY) {
        this._gameConfig.postbackManagerKey = process.env.POSTBACK_MANAGER_KEY;
      }
      if (process.env.ZOOM_API_ENDPOINT) {
        this._gameConfig.zoomAPIEndpoint = process.env.ZOOM_API_ENDPOINT;
      }
      if (process.env.ZOOM_SDK_CAPABILITIES) {
        if (!this._gameConfig.zoomSDKConfig) {
          this._gameConfig.zoomSDKConfig = { capabilities: [] };
        }
        this._gameConfig.zoomSDKConfig.capabilities =
          process.env.ZOOM_SDK_CAPABILITIES.split(',');
      }
      if (process.env.ZOOM_MEETING_CLIENT_USER_PROPS) {
        this._gameConfig.zoomMeetingClientUserProperties =
          process.env.ZOOM_MEETING_CLIENT_USER_PROPS.split(',');
      }
      if (process.env.ZOOM_MAIN_CLIENT_USER_PROPS) {
        this._gameConfig.zoomMainClientUserProperties =
          process.env.ZOOM_MAIN_CLIENT_USER_PROPS.split(',');
      }
    } catch {
      // Swallow error - `process` might be undefined
    }

    if (!this.appID) {
      throw Error('appID is not defined');
    }

    // Default to `UTC` if Amplitude time zone is undefined, invalid or not supported by client browser (IE only supports `UTC`):
    this._gameConfig.amplitudeTimeZone =
      this._gameConfig.amplitudeTimeZone || 'UTC';
    try {
      new Date().toLocaleString('en-US', {
        timeZone: this._gameConfig.amplitudeTimeZone,
      });
    } catch (error) {
      if (this._gameConfig.isDevelopment) {
        console.warn(
          `Invalid Amplitude time zone in GCInstant.initializeAsync: ${this._gameConfig.amplitudeTimeZone}. Defaulting to UTC`,
        );
      }
      this._gameConfig.amplitudeTimeZone = 'UTC';
    }

    if (this._gameConfig.isDevelopment) {
      console.warn(
        'Running GCInstant in development mode - make sure to disable development mode in production.',
      );
    }
  }

  _initialize(playerID: string): Promise<void> {
    if (!playerID) {
      throw new Error('playerID is falsey!');
    }

    if (!this._gameConfig) {
      throw Error(
        'Game configuration is not defined: call readConfiguration before initializing',
      );
    }

    this.playerID = playerID;
    this._initialized = true;
    this.getEntryPointData();

    this.setLoadingProgress(0);

    if (this.platformID === 'fb') {
      analytics.addPlatformService(this);
    }

    analytics.initialize(playerID, {
      ...this._gameConfig,
      appID: this.appID, // this is to work around years-old technical debt related to appID sometimes being an object in the config
    });
    events.trackPlatformInitSuccess();

    return this.refreshPlayerSignature().then(() => {
      this.trackTimeTo('getSignedPlayerInfoAsync');
    });
  }

  /**
   * Enables payload encoding.
   * More info in [Payload Encoding](https://docs.dev.gc-internal.net/gcinstant-replicant-extensions/index.html#payload-encoding).
   *
   * @throws when called after `startGameAsync`.
   *
   * @param dataCodec encode and decode functions for data sent to platform functions and for received payloads.
   */
  setDataCodec(dataCodec: DataCodec): void {
    if (this.didCallStartGameAsync) {
      throw new Error(
        `Set an encoder and decoder before calling 'startGameAsync'.` +
          ` Otherwise, some encoded payloads may not be decoded correctly.`,
      );
    }

    this.dataCodec = dataCodec;
  }

  /**
   * Set friends analytics data, typically fetched with the `getFriendAnalytics`
   * async getter from `@play-co/gcinstant-replicant-extensions`.
   *
   * Call once per session before `sendEntryFinalAnalytics`.
   */
  setFriendAnalytics(friendAnalytics: {
    activeFriendCounts: ActiveFriendCounts;
    clusterCounts: ClusterCounts;
  }): void {
    this.throwIfNotInitialized();

    const cluster = friendAnalytics.clusterCounts;
    const friend = friendAnalytics.activeFriendCounts;

    function getRatio(
      numerator: number,
      denominator: number,
    ): number | undefined {
      const ratio = numerator / denominator;
      return Number.isFinite(ratio) ? ratio : undefined;
    }

    analytics.setUserProperties({
      lastEntryActiveClusterSize1D: cluster.clusterSize1D,
      lastEntryActiveClusterSize3D: cluster.clusterSize3D,
      lastEntryActiveClusterSize7D: cluster.clusterSize7D,
      lastEntryActiveClusterSize14D: cluster.clusterSize14D,
      lastEntryActiveClusterSize30D: cluster.clusterSize30D,
      lastEntryActiveClusterSize90D: cluster.clusterSize90D,
      lastEntryActiveClusterConnections1D: cluster.clusterConnections1D,
      lastEntryActiveClusterConnections3D: cluster.clusterConnections3D,
      lastEntryActiveClusterConnections7D: cluster.clusterConnections7D,
      lastEntryActiveClusterConnections14D: cluster.clusterConnections14D,
      lastEntryActiveClusterConnections30D: cluster.clusterConnections30D,
      lastEntryActiveClusterConnections90D: cluster.clusterConnections90D,
      lastEntryActiveClusterConnectedness1D: cluster.clusterConnectedness1D,
      lastEntryActiveClusterConnectedness3D: cluster.clusterConnectedness3D,
      lastEntryActiveClusterConnectedness7D: cluster.clusterConnectedness7D,
      lastEntryActiveClusterConnectedness14D: cluster.clusterConnectedness14D,
      lastEntryActiveClusterConnectedness30D: cluster.clusterConnectedness30D,
      lastEntryActiveClusterConnectedness90D: cluster.clusterConnectedness90D,

      lastEntryActiveFriendCount1D: friend.activeFriendCount1D,
      lastEntryActiveFriendCount3D: friend.activeFriendCount3D,
      lastEntryActiveFriendCount7D: friend.activeFriendCount7D,
      lastEntryActiveFriendCount14D: friend.activeFriendCount14D,
      lastEntryActiveFriendCount30D: friend.activeFriendCount30D,
      lastEntryActiveFriendCount90D: friend.activeFriendCount90D,
      'lastEntryActiveFriendRatio1/3': getRatio(
        friend.activeFriendCount1D,
        friend.activeFriendCount3D,
      ),
      'lastEntryActiveFriendRatio1/7': getRatio(
        friend.activeFriendCount1D,
        friend.activeFriendCount7D,
      ),
      'lastEntryActiveFriendRatio3/7': getRatio(
        friend.activeFriendCount3D,
        friend.activeFriendCount7D,
      ),
      'lastEntryActiveFriendRatio7/30': getRatio(
        friend.activeFriendCount7D,
        friend.activeFriendCount30D,
      ),
    });
  }

  /**
   * Called after `startGameAsync` to
   * - handle the minority of clients whose entry data is not ready right
   *   after `initializeAsync`,
   * - decode entry data in case a `dataCodec` is set, and
   * - set default entry data values.
   */
  protected async _prepareEntryData(): Promise<void> {
    const prepareEntryDataInternal = async () => {
      this.entryData = this._getEntryPointDataForce() || this.entryData;

      const decodedEntryData = await this.dataCodec.decode(this.entryData);
      if (decodedEntryData) {
        const isAppSwitch = this.entryData.isAppSwitch;

        this.entryData = decodedEntryData;

        // Avoid overriding `isAppSwitch` which is not part of the payload:
        if (isAppSwitch !== undefined) {
          this.entryData.isAppSwitch = isAppSwitch;
        }
      }

      const entryData = this.entryData;

      if (this.platformID === 'fb') {
        // TODO: deprecate fb part
        entryData.gameID =
          entryData.gameID ||
          entryData.sourceGameID ||
          this._gameConfig.shortName;
        entryData.gameVersion =
          entryData.gameVersion ||
          entryData.sourceGameVersion ||
          entryData.sourceVersion;
        entryData.gamePlayerID =
          entryData.gamePlayerID ||
          entryData.sourceGamePlayerID ||
          this.playerID;
        entryData.gameFeature =
          entryData.gameFeature ||
          entryData.sourceGameFeature ||
          entryData.feature;
        entryData.gameLevel =
          entryData.gameLevel ||
          entryData.sourceGamePlayerLevel ||
          entryData.sourceLevel;
      } else {
        entryData.gameID = entryData.gameID || this._gameConfig.shortName;
        entryData.gameVersion =
          entryData.gameVersion || entryData.sourceVersion;
        entryData.gamePlayerID = entryData.gamePlayerID || this.playerID;
        entryData.gameFeature = entryData.gameFeature || entryData.feature;
        entryData.gameLevel = entryData.gameLevel || entryData.sourceLevel;
      }
    };
    return prepareEntryDataInternal()
      .then(this._resolveEntryData.resolve)
      .catch((err) => {
        this._resolveEntryData.reject(err);
        throw err;
      });
  }

  /**
   * Starts the game once assets are loaded and the backend is ready.
   *
   * @throws If called more than once, or if storage adapter is not configured when in production mode.
   */
  async startGameAsync(): Promise<void> {
    this.throwIfNotInitialized();
    this.throwIfStartGameAsyncIsCalledMultipleTimes();
    this.throwIfStorageAdapterIsNotConfigured();

    try {
      await this.startGameAsyncInner();

      this._gameStarted = true;
      attachFirstTapListener();
      void this._sendEntryAnalytics();
    } catch (error) {
      events.trackEntryFailed(unwrapNestedError(error));
      throw unwrapNestedError(error);
    }
  }

  /** Platform-specific game start implementation, called by the public `startGameAsync` method. */
  protected abstract startGameAsyncInner(): Promise<void>;

  /** Set a callback that is invoked before each context switch attempt. */
  beforeContextSwitch(callback: () => void): void {
    this.beforeContextSwitchCallback = callback;
  }

  /** Set a callback that is invoked after each successful context switch. */
  afterContextSwitch(callback: () => void): void {
    this.afterContextSwitchCallback = callback;
  }

  onPause(onPause?: () => void): void {
    // TODO
    this._onPause = onPause;
  }

  onPaymentsReady(onPaymentsReady: () => void): void {
    if (this._isPaymentsReady) {
      onPaymentsReady();
      return;
    }
    this._onPaymentsReady = onPaymentsReady;
  }

  /**
   * In order for the devkit-instant purchase functions to work they need a
   * means of applying a purchased product to a users account. Because this
   * product and possibly persistantce layer are unique to the game itself the
   * game must provide devkit-instant with a callback that performs those game
   * specific purchase tasks and resovles when finished.
   * @param onProvisionProductAsync Returns a promise that resolves
   * after the game has successfully provisioned the users account with the
   * purchased product.
   */
  onProvisionProductAsync(
    onProvisionProductAsync: ProvisionProductAsyncCallback,
  ): void {
    this._onProvisionProductAsync = onProvisionProductAsync;
  }

  /**
   * Helper function to retrive Product metadata from the catalog by productId.
   * @param productId Id of the product to retrieve from the catalog.
   */
  _getProduct(productId: string): Product | undefined {
    if (this.catalog.length === 0) return undefined;

    for (let p = 0; p < this.catalog.length; p++) {
      const product = this.catalog[p];
      if (product.id === productId) {
        return product;
      }
    }
    return undefined;
  }

  checkCanPlayerMatchAsync(): Promise<boolean> {
    return Promise.resolve(false);
  }

  matchPlayerAsync(
    matchTag?: string,
    switchContextWhenMatched?: boolean,
    offlineMatch?: boolean,
  ): Promise<any> {
    return Promise.reject(new Error('not implemented'));
  }

  quit(): void {}
  getSDKVersion(): void {}
  setLoadingProgress(value: number, bytesLoaded?: number): void {}
  dismissLoadingScreenAsync(): Promise<void> {
    return Promise.resolve();
  }

  getCatalogAsync(): Promise<Product[]> {
    return Promise.reject(new Error('not implemented'));
  }

  setSessionData(data: unknown): void {
    throw new Error('not implemented');
  }

  /**
   * Initializes in app purchase API
   * TODO: move onPaymentsReady and onProvisionProductAsync as arguments here
   * @param options.productIDs product id list as found on the app store for given platform OS.
   */
  async iapInit(options: { productIDs: string[] }): Promise<void> {
    // optional for some platforms therefore resolve by default
  }

  purchaseAsync(
    productID: string,
    payload?: string,
  ): Promise<{ purchase: Purchase; product: Product }> {
    return Promise.reject(new Error('not implemented'));
  }

  getDataAsync(keys: string[]): Promise<Record<string, any>> {
    const data = {};
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      data[key] = getOrSetLocalStorageData(`${PLAYER_DATA_PREFIX}:${key}`);
    }
    return Promise.resolve(data);
  }

  setDataAsync(data: Record<string, any>): Promise<void> {
    return Promise.resolve().then(() => {
      Object.keys(data).forEach((key) => {
        setLocalStorageData(`${PLAYER_DATA_PREFIX}:${key}`, data[key]);
      });
    });
  }

  /**
   * Note that GCInstant fetches platform friends automatically on initialization:
   * use `Platform.friends` instead of this method to avoid duplicate requests.
   */
  getFriendsAsync(): Promise<Friend[]> {
    return Promise.resolve(this.friends);
  }

  filterFriendsAsync(filters: unknown): Promise<Friend[]> {
    return Promise.resolve(this.friends);
  }

  getPlayersAsync(): Promise<Friend[]> {
    return Promise.resolve(this.friends);
  }

  getContextPlayersAsync(): Promise<ContextPlayer[]> {
    return Promise.resolve([]); // TODO
  }

  flushDataAsync(): Promise<void> {
    return Promise.resolve();
  }

  getSignedPlayerInfoAsync(requestPayload: string): Promise<SignedPlayerInfo> {
    return Promise.resolve(new SignedPlayerInfo());
  }

  /**
   * Fetch a new player signature from platform and update `this.playerSignature`.
   * @returns New player signature.
   */
  async refreshPlayerSignature(): Promise<string> {
    this.throwIfNotInitialized();

    return this.getSignedPlayerInfoAsync('LOGIN_REQUEST').then(
      (signedPlayerInfo) => {
        this.playerSignature = signedPlayerInfo.signature;

        return this.playerSignature;
      },
    );
  }

  switchGameAsync(
    gameId: string,
    data: { [key: string]: unknown },
  ): Promise<void> {
    return Promise.reject(new Error(''));
  }

  /**
   * Check if the client can switch to its Native (Android/iOS) Game.
   *
   * @returns Whether or not client can switch to Native game.
   */
  async canSwitchNativeGameAsync(): Promise<boolean> {
    return false;
  }

  /**
   * Request that the client switch to its Native (Android/iOS) Game.
   * The API will reject if the switch fails - else, the client will open the Game or Store.
   *
   * @param data This will be set as the entrypoint data for the game being switched to.
   */
  async switchNativeGameAsync(data?: { [key: string]: any }): Promise<void> {
    throw new Error('GCInstant.switchNativeGameAsync: Not implemented');
  }

  canCreateShortcutAsync(): Promise<boolean> {
    return Promise.resolve().then(() => (this.canCreateShortcut = false));
  }

  createShortcutAsync(feature?: string): Promise<void> {
    return Promise.reject(new Error(''));
  }

  canSubscribeBotAsync(): Promise<boolean> {
    return Promise.resolve(this.canSubscribeToBot);
  }

  subscribeBotAsync(
    subscriptionFlow?: Promise<void>,
    analytics?: CommonAnalytics,
  ): Promise<void> {
    return subscriptionFlow || Promise.resolve();
  }

  /**
   * Get an interstitial ad instance.
   *
   * **Use the high-level `Platform.ads` interface** instead for basic ad use cases.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  getInterstitialAdAsync(placementID: string): Promise<AdInstance> {
    return Promise.resolve(new VideoAdMock(placementID));
  }

  /**
   * Get a rewarded video ad instance.
   *
   * **Use the high-level `Platform.ads` interface** instead for basic ad use cases.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  getRewardedVideoAsync(placementID: string): Promise<AdInstance> {
    return Promise.resolve(new VideoAdMock(placementID));
  }

  /**
   * For the LINK platform, this will send a message to the chosen user.
   * That message is configurable via messageOpts, otherwise a default will be used.
   */
  chooseContextAsync(opts: ChooseContextOptions): Promise<void> {
    return Promise.resolve();
  }

  switchContextAsync(
    contextID: string,
    analyticsData: CommonAnalytics,
  ): Promise<void> {
    this.contextID = contextID;
    return Promise.resolve();
  }

  /**
   * @param uiOpts Options for the LINE platform confirmation UI dialog. Only enabled on the LINE platform.
   */
  createContextAsync(
    playerID: string,
    analyticsData: CommonAnalytics,
    uiOpts?: ConfirmationOptions,
  ): Promise<void> {
    return Promise.resolve();
  }

  isContextPublicAsync(contextID: string): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * We recommend using the `canUseSquads` property instead where possible. Returns whether the current user is eligible to use squads. Available on
   * the FB platform only. Note that this feature is in closed beta and must be explicitly enabled for the FB app.
   */
  canUseSquadsAsync(): Promise<boolean> {
    return Promise.reject(new Error('not implemented'));
  }

  /**
   * Create a new squad and switch to squad context.
   *
   * - FB: opens a UI dialog for inputting squad options.
   * - LINE: does not open a UI dialog - use the `opts` parameter to set squad options.
   *
   * Only enabled on the Facebook and LINE platforms.
   *
   * @example
   * ```ts
   * // Check if squads feature is available:
   * if (!GCInstant.canUseSquads) {
   *   return;
   * }
   *
   * // Create a squad:
   * const squad = await GCInstant.createSquadAsync(createSquadOpts);
   *
   * // Once in a squad context, send a squad message:
   * await GCInstant.updateAsync(updateOpts);
   *
   * // Join an existing squad:
   * const squad = await GCInstant.getSquadAsync(squadIdOrContextId);
   * await squad.joinSquadAsync(joinOpts);
   * ```
   *
   * @throws `UNAUTHORIZED` if user has not granted OpenChat permissions on LINE.
   * @throws `USER_INPUT` if user dismissed the UI dialog on Facebook.
   * @throws If the platform SDK call fails for any other reason.
   */
  createSquadAsync(opts: CreateSquadAsyncOptions): Promise<Squad> {
    return Promise.reject(new Error('not implemented'));
  }

  /**
   * Retrieve a squad.
   *
   * Only enabled on the Facebook and LINE platforms.
   *
   * @throws `GAMING_SQUAD_NOT_FOUND` if squad is not found.
   *
   * @param squadOrContextId Squad ID or squad context ID.
   */
  getSquadAsync(squadOrContextId: string): Promise<Squad> {
    return Promise.reject(new Error('not implemented'));
  }

  /**
   * Only enabled on the Facebook platform.
   *
   * @returns a promise that resolves with an array of the gaming squads the current player is in.
   */
  getPlayerSquadsAsync(): Promise<Squad[]> {
    return Promise.reject(new Error('not implemented'));
  }

  /**
   * Returns a Tournament object or rejects if no tournament was found in current context.
   */
  async getTournamentAsync(): Promise<Tournament> {
    return Promise.reject(new Error('not implemented'));
  }

  /**
   * Opens the tournament creation dialog if the player is not currently in a tournament session.
   *
   * @returns tournament object if creation success & switched into social context of the Tournament; otherwise the promise is rejected with error.
   */
  createTournamentAsync(opts: CreateTournamentOptions): Promise<Tournament> {
    return Promise.reject('GCInstant.createTournamentAsync: Not implemented');
  }

  /**
   * Must be in context of that Tournament.
   *
   * @returns Promise is resolved if the tournament is shared; otherwise the promise is rejected with error.
   */
  shareTournamentAsync(opts: ShareTournamentOptions): Promise<Tournament> {
    return Promise.reject('GCInstant.shareTournamentAsync: Not implemented');
  }

  /**
   * Updates a user's score in a tournament and does not prompt the tournament creation/share dialog.
   *
   * @param score The user's current score in the tournament
   * @param analyticsData Any custom analytics the game wants to track.
   *
   * @returns The promise is resolved to void if post score success; otherwise the promise is rejected with error.
   */
  postTournamentScoreAsync(
    score: number,
    analyticsData?: { [key: string]: any },
  ): Promise<void> {
    return Promise.reject(
      'GCInstant.postTournamentScoreAsync: Not implemented',
    );
  }

  /**
   * Set a getter function for fetching other players' entry timestamps.
   * Entry timestamps are fetched when posting events like `EntryConversion`
   * and `UpdateReceipt` to another player's event stream.
   *
   * @param entryTimestampsGetter A function that returns a promise of entry timestamps for given player IDs.
   * In case of missing data, omit player IDs from the return value.
   */
  setEntryTimestampsGetter(entryTimestampsGetter: EntryTimestampsGetter): void {
    this.entryTimestampsGetter = entryTimestampsGetter;
  }

  protected async getTemporalUserProperties(playerIds: string[]): Promise<{
    [playerId: string]: AnalyticsProperties.TemporalUserProperties;
  }> {
    if (!playerIds.length) {
      return {};
    }

    if (!this.entryTimestampsGetter) {
      return {};
    }

    try {
      const entryTimestamps = await this.entryTimestampsGetter(playerIds);

      const now = globalTime.getNow();
      const temporalUserProps: {
        [playerId: string]: AnalyticsProperties.TemporalUserProperties;
      } = {};

      for (const playerId of playerIds) {
        if (
          !entryTimestamps[playerId] ||
          Object.keys(entryTimestamps[playerId]).length === 0
        ) {
          continue;
        }

        const { firstEntryAt, lastEntryAt, zeroEntryAt, lastAdEntryAt } =
          entryTimestamps[playerId];

        const isValid =
          (!firstEntryAt || typeof firstEntryAt === 'number') &&
          typeof lastEntryAt === 'number' &&
          (!zeroEntryAt || typeof zeroEntryAt === 'number') &&
          (!lastAdEntryAt || typeof lastAdEntryAt === 'number');

        if (isValid) {
          temporalUserProps[playerId] = {
            daysElapsedLastEntry: (now - lastEntryAt) / dayInMs,
          };

          if (firstEntryAt) {
            temporalUserProps[playerId].daysElapsedFirstEntry =
              (now - firstEntryAt) / dayInMs;
            temporalUserProps[playerId].daysSinceInstall = Math.floor(
              (now - firstEntryAt) / dayInMs,
            );
          }

          if (lastAdEntryAt) {
            temporalUserProps[playerId].daysSinceLastAdEntry = Math.floor(
              (now - lastAdEntryAt) / dayInMs,
            );
          }

          if (zeroEntryAt) {
            temporalUserProps[playerId].$lastEntryElapsedDaysZeroEntry =
              (now - zeroEntryAt) / dayInMs;
          }
        } else {
          events.trackGetEntryTimestampsFailure(
            Error('Invalid entry timestamps'),
            {
              entryTimestamps: entryTimestamps[playerId],
              receiverID: playerId,
            },
          );
        }
      }

      return temporalUserProps;
    } catch (error) {
      events.trackGetEntryTimestampsFailure(error);
      return {};
    }
  }

  // TODO inApp purchases

  // TODO session

  /**
   * Post an update to the current context, for example a message in a chat thread.
   *
   * Define `opts.receiver` in order to track [`UpdateReceiptSuccess`](https://docs.dev.gc-internal.net/gcinstant/events-reference/index.html#updatereceiptsuccess).
   *
   * @throws `INVALID_PARAM` error on the LINE platform `opts.template` is not defined.
   * @throws If the platform SDK call fails for any other reason.
   */
  updateAsync(opts: UpdateAsyncOptions): Promise<UpdateAsyncResult> {
    console.log('FBInstant.updateAsync', opts);
    const payloadData = this._populateNotificationPayload(opts.data, 'UPDATE');
    return Promise.resolve({ payloadData, returnValue: undefined });
  }

  isContextSizeBetween(
    min?: number,
    max?: number,
  ): FBInstant.ContextSizeResponse | null {
    return null;
  }

  /**
   * Open a UI dialog to let the user share specified content.
   *
   * Exact sharing behavior varies per platform:
   * - FB: creates a post on the users's Facebook wall.
   * - Link and Viber: opens a friend picker UI dialog and sends a message to selected friends.
   * - LINE: opens a friend picker UI dialog and sends a message to selected friends, unless
   *  `opts.receiverIDs` is specified in which case sends message to specified receivers.
   *
   * @throws `INVALID_PARAM` error on the LINE platform if `opts.receiverIDs`
   * includes a non-friend ID or `opts.template` is not defined.
   * @throws `USER_INPUT` error on the LINE platform if user cancels the UI dialog before completing the share.
   * Link and Viber platforms do not throw the error, and on Facebook the error is only thrown if `opts.switchContext` is `true`.
   * @throws If the platform SDK call fails for any other reason.
   *
   * @returns number of receivers on Viber, receiver IDs on LINE and `undefined` on other platforms.
   */
  shareAsync(
    opts: ShareAsyncOptions,
  ): Promise<void | { sharedCount: number } | { receiverIDs: string[] }> {
    console.log('FBInstant.shareAsync', opts);
    return Promise.resolve();
  }

  /**
   * This invokes a dialog that contains a custom game link that users can copy
   * to their clipboard, or share. A blob of data can be attached
   * to the custom link - game sessions initiated from the link will be able to access
   * `platform.getEntryPointData()`. This data should be less than or equal to
   * 1000 characters when stringified. The provided text and image will be used to
   * generate the link preview , with the game name as the title of the preview.
   *
   * The text is recommended to be less than 44 characters.
   *
   * The image is recommended to either be a square or of the aspect ratio 1.91:1.
   * The returned promise will resolve when the dialog is closed regardless if the
   * user actually shared the link or not.
   */
  shareLinkAsync(opts: ShareLinkAsyncOptions): Promise<void> {
    console.log('FBInstant.shareLinkAsync', opts);
    return Promise.resolve();
  }

  /**
   * Only enabled on the Facebook and LINE platforms.
   *
   * @throws `INVALID_PARAM` error on the LINE platform if `opts.receiverIDs`
   * includes a non-friend ID  or `opts.template` is not defined.
   * @throws `USER_INPUT` error on the LINE platform if user closes the friend
   * selection or confirmation dialog without sending any messages. Does not
   * throw `USER_INPUT` on the Facebook platform.
   *
   * @returns receiver IDs on LINE and void on other platforms.
   */
  inviteAsync(
    opts: InviteAsyncOptions,
  ): Promise<void | { receiverIDs: string[] }> {
    console.log('FBInstant.inviteAsync', opts);
    return Promise.resolve();
  }

  /**
   * Returns any data object associated with the entry point that the game was launched from.
   *
   * The contents of the object are developer-defined, and can occur from entry points on
   * different platforms. This will return null for older mobile clients, as well as when
   * there is no data associated with the particular entry point.
   */
  getEntryPointData(): AnalyticsProperties.EntryData {
    if (this.entryData !== null) {
      return this.entryData;
    }

    this.entryData = this._getEntryPointDataForce();
    return this.entryData;
  }

  /** Get platform entry point data, skipping cache. */
  protected _getEntryPointDataForce(): AnalyticsProperties.EntryData {
    return {} as AnalyticsProperties.EntryData;
  }

  /**
   * Decodes URL encoded entry data. URL looks like `https://www.example.com?payload=%7B%22key%22%3A%22value%22%7D`.
   *
   * To encode entry data use `encodeURIComponent(JSON.stringify(data))` (standard ES5 function) and append it to the game's URL
   *
   * To get entry data within the game's code, use
   *    platform.getEntryDataAsync().then(entryData => {
   *      // Check entryData here
   *    });
   *
   * NOTE that only the simulator/mock platform and LINE use URL encoded entry data, other platforms have their own ways
   * however we use it in local facebook env development too to add custom payload to simulate prod payloaded entrypoints
   */
  _getQueryEntryData(): AnalyticsProperties.EntryData {
    // Getting entry payload from url
    // Hack: for some reason we need to decode query string twice
    const queryString = decodeURIComponent(
      decodeURIComponent(window.location.search.slice(1)),
    );

    const params = queryString.split('&');
    const paramMap: any = {};
    for (let p = 0; p < params.length; p++) {
      const param = params[p].split('=');
      const key = param[0];
      const value = param[1];
      paramMap[key] = value;

      // hack to get template info when badly generated
      if (value === 'null' || value === undefined) {
        paramMap.tID = key;
      }
    }

    // Hack to fix param value issue where an "=" is sometimes trailing
    for (const entryProperty in paramMap) {
      const entryValue = paramMap[entryProperty];
      if (entryValue && entryValue.endsWith('=')) {
        paramMap[entryProperty] = entryValue.slice(0, entryValue.length - 1);
      }
    }

    const payload = paramMap.payload;
    const entryData = payload ? JSON.parse(payload) : {};
    return entryData;
  }

  async getEntryPointAsync(): Promise<string> {
    this.entryPointName = 'MOCK';
    return this.entryPointName;
  }

  getEntryDataAsync(): Promise<AnalyticsProperties.EntryData> {
    return this._entryDataPromise.then(() => this.entryData);
  }

  /**
   * Returns timestamp of previous entry, or `null` if currently on first entry.
   * @throws if called before resolving `loadStorage`.
   */
  getPreviousEntryTimestamp(): number | null {
    if (this.storage.entry.count === 0) {
      throw Error(
        'Resolve loadStorage() before getting previous entry timestamp',
      );
    }

    return this.previousEntryTimestamp;
  }

  getLeaderboardAsync(leaderboardId: string): Promise<FBInstant.Leaderboard> {
    return Promise.resolve({} as FBInstant.Leaderboard);
  }

  postSessionScoreAsync(
    score: number,
    analyticsData?: AnalyticsProperties.AnyPayload,
  ): Promise<void> {
    return Promise.reject(
      Error('GCInstant.postSessionScoreAsync: Not implemented'),
    );
  }

  /**
   * Get all permissions enabled for the player.
   *
   * Only enabled on the Snapchat platform.
   *
   * @returns permissions K: the permission, V: if the permission is enabled for the player.
   */
  async getPermissionsAsync(): Promise<Record<string, boolean>> {
    return Promise.reject(
      new Error('GCInstant.requestPermissionAsync: Not implemented'),
    );
  }

  /**
   * Request additonal permissions from the platform.
   *
   * Only enabled on the Snapchat platform.
   *
   * @param scope - The permission to request from the user. There is only one requestable permission: ACTIVITY_VISIBILITY
   * @returns true if user accepted permission or false if denied permission.
   */
  async requestPermissionAsync(scope: 'ACTIVITY_VISIBILITY'): Promise<boolean> {
    return Promise.reject(
      new Error('GCInstant.requestPermissionAsync: Not implemented'),
    );
  }

  /**
   * Return contextual payload properties that the Platform can provide.
   *
   * Should be called after `sendEntryFinalAnalytics`.
   */
  getPlatformPayloadData(): Partial<AnalyticsProperties.EntryData> {
    const firstProps = this.storage
      .analytics as AnalyticsProperties.FirstEntryUserProperties;
    const lastProps = analytics.getUserProperties();

    const abTestPayloadProperties = {
      ...this.externalABTests.getPayloadProperties(),
    };

    if (this.abTests?.config) {
      this.abTests.applyPayloadProperties(abTestPayloadProperties);
    }

    const now = globalTime.getNow();

    // Prune referral chain timestamps and append current timestamp:
    const maxReferralChainDuration = duration({ days: 1 });
    const sourceChainTimestamps = (this.entryData.sourceChainTimestamps || [])
      .filter((timestamp) => now - timestamp <= maxReferralChainDuration)
      .concat(now);

    const date = new Date(now).toISOString().slice(0, 10).replace(/-/g, ''); // `date` uses UTC instead of `amplitudeTimeZone`
    const { dateHour, dateTime, hour } = getAnalyticsTimeProperties(
      now,
      this._gameConfig.amplitudeTimeZone!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
    );

    return {
      ...abTestPayloadProperties,
      ABTag: lastProps.ABtag,

      country: this.countryName,
      date,
      dateHour,
      dateTime,
      daysSinceInstall: lastProps.daysSinceInstall,
      hour,
      playerID: this.playerID,
      sourceChainTimestamps,
      sourceVersion: this._gameConfig.version,
      sourceOS: userAgent.os.name,
      timestamp: now,

      $firstConnectedPlayerCount: firstProps.firstEntryConnectedPlayerCount,
      $firstEntryChannel: firstProps.$firstEntryChannel,
      $firstEntryDate: firstProps.firstEntryDate,
      $firstEntryPointName: firstProps.firstEntryPointName,
      $firstEntryVersion: firstProps.firstEntryVersion,

      $lastConnectedPlayerCount: lastProps.lastConnectedPlayerCount,
      $lastEntrySessionMaturityRange: lastProps.$lastEntrySessionMaturityRange,
      $lastEntryChannel: lastProps.$lastEntryChannel,
      $lastEntryPlatform: lastProps.$lastEntryPlatform,
      $lastEntryPointName: lastProps.lastEntryPointName,
      $lastEntryVersion: lastProps.lastEntryVersion,

      $firstEntryGeneration: firstProps.$firstEntryGeneration,
      $zeroEntryChannel: firstProps.$zeroEntryChannel,
      $zeroEntryDatetime: firstProps.$zeroEntryDatetime,
      $zeroEntryDate: firstProps.$zeroEntryDate,
      $zeroEntryAdCampaignName: firstProps.$zeroEntryAdCampaignName,
      $zeroEntryAdSetName: firstProps.$zeroEntryAdSetName,
      $zeroEntryAdCreativeName: firstProps.$zeroEntryAdCreativeName,
      $zeroEntryAdNetworkName: firstProps.$zeroEntryAdNetworkName,
      zeroEntrySourcePlayerID: firstProps.zeroEntrySourcePlayerID,

      $entryCount: lastProps.entryCount,

      lastEntryActiveClusterSize1D: lastProps.lastEntryActiveClusterSize1D,
      lastEntryActiveClusterSize3D: lastProps.lastEntryActiveClusterSize3D,
      lastEntryActiveClusterSize7D: lastProps.lastEntryActiveClusterSize7D,
      lastEntryActiveClusterSize14D: lastProps.lastEntryActiveClusterSize14D,
      lastEntryActiveClusterSize30D: lastProps.lastEntryActiveClusterSize30D,
      lastEntryActiveClusterSize90D: lastProps.lastEntryActiveClusterSize90D,
      lastEntryActiveClusterConnections1D:
        lastProps.lastEntryActiveClusterConnections1D,
      lastEntryActiveClusterConnections3D:
        lastProps.lastEntryActiveClusterConnections3D,
      lastEntryActiveClusterConnections7D:
        lastProps.lastEntryActiveClusterConnections7D,
      lastEntryActiveClusterConnections14D:
        lastProps.lastEntryActiveClusterConnections14D,
      lastEntryActiveClusterConnections30D:
        lastProps.lastEntryActiveClusterConnections30D,
      lastEntryActiveClusterConnections90D:
        lastProps.lastEntryActiveClusterConnections90D,
      lastEntryActiveClusterConnectedness1D:
        lastProps.lastEntryActiveClusterConnectedness1D,
      lastEntryActiveClusterConnectedness3D:
        lastProps.lastEntryActiveClusterConnectedness3D,
      lastEntryActiveClusterConnectedness7D:
        lastProps.lastEntryActiveClusterConnectedness7D,
      lastEntryActiveClusterConnectedness14D:
        lastProps.lastEntryActiveClusterConnectedness14D,
      lastEntryActiveClusterConnectedness30D:
        lastProps.lastEntryActiveClusterConnectedness30D,
      lastEntryActiveClusterConnectedness90D:
        lastProps.lastEntryActiveClusterConnectedness90D,

      realtimeActiveFriendCount1D: lastProps.lastEntryActiveFriendCount1D,
      realtimeActiveFriendCount3D: lastProps.lastEntryActiveFriendCount3D,
      realtimeActiveFriendCount7D: lastProps.lastEntryActiveFriendCount7D,
      realtimeActiveFriendCount14D: lastProps.lastEntryActiveFriendCount14D,
      realtimeActiveFriendCount30D: lastProps.lastEntryActiveFriendCount30D,
      realtimeActiveFriendCount90D: lastProps.lastEntryActiveFriendCount90D,
    };
  }

  /**
   * This populates a notification payload with information that the Platform can provide
   * and that is required for analytics.
   */
  protected _populateNotificationPayload<T extends { [key: string]: any }>(
    payload: T,
    $channel: AnalyticsProperties.Channel,
  ): T & AnalyticsProperties.Payload {
    // Session sequence
    sessionSequence += 1;

    // Absolute sequence
    const absoluteSequence = this.storage.absoluteSequence + 1;
    this.storage.absoluteSequence = absoluteSequence;

    // Feature sequences
    const feature = payload.feature;

    // Feature session sequence
    const featureSessionSequence = (featureSessionSequences[feature] || 0) + 1;
    featureSessionSequences[feature] = featureSessionSequence;

    // Feature absolute sequence
    const featureAbsoluteSequence =
      (this.storage.featureAbsoluteSequences[feature] || 0) + 1;
    this.storage.featureAbsoluteSequences[feature] = featureAbsoluteSequence;

    // Unique payload identifier
    const tracerKey = [
      this.playerID,
      globalTime.getNow(),
      Math.random().toString(36).substr(2, 5),
    ].join('-');

    return {
      ...payload,
      ...(this.getPlatformPayloadData() as AnalyticsProperties.Payload),
      UID: payload.asset + '/' + payload.text + '/' + payload.button,
      sessionSequence: sessionSequence,
      absoluteSequence: absoluteSequence,
      featureSessionSequence: featureSessionSequence,
      featureAbsoluteSequence: featureAbsoluteSequence,
      tracerKey,
      $channel,
    };
  }

  /**
   * Loads platform storage.
   *
   * When using a storage adapter, make sure to configure the adapter
   * before invoking this method.
   *
   * Subsequent calls return the initial call's cached return value, so this method can
   * be called several times without extra side-effects.
   *
   * @throws If storage adapter is not configured when in production mode.
   *
   * @returns A promise that resolves once storage is loaded.
   */
  async loadStorage(): Promise<void> {
    this.throwIfStorageAdapterIsNotConfigured();

    if (this._loadStoragePromise) {
      return this._loadStoragePromise;
    }

    this._loadStoragePromise = this.storage.load().then(() => {
      if (this.shouldIncreaseEntryCount()) {
        this.storage.entry.count += 1;
      }

      if (this.storage.entry.last) {
        this.previousEntryTimestamp = this.storage.entry.last;
      }

      // Instantiate ABTests:
      const appName = this._gameConfig.shortName;
      this.abTests = new ABTests(this.playerID, appName, this.storage, () =>
        this.externalABTests.getABTag(),
      );
    });

    return this._loadStoragePromise;
  }

  protected _createEntryPromise(): Promise<void> {
    return Promise.resolve();
  }

  private _setEntryDataUserProperties(
    extraUserProperties?: Record<string, any>,
  ) {
    const userProperties = { ...extraUserProperties };
    const entryData = this.entryData;

    userProperties.lastEntryAsset = entryData.asset;
    userProperties.lastEntryText = entryData.text;
    userProperties.lastEntryButton = entryData.button;
    userProperties.lastEntryUID = entryData.UID;
    userProperties.lastEntrySourceFeature = entryData.feature;
    userProperties.lastEntrySourceVersion = entryData.sourceVersion;
    userProperties.lastEntrySourceGameFeature = entryData.gameFeature;
    userProperties.lastEntrySourceGameID = entryData.gameID;
    userProperties.lastEntrySourceGamePlayerID = entryData.gamePlayerID;
    userProperties.lastEntrySourcePlayerID = entryData.playerID;
    userProperties.lastEntryAdNetwork = entryData.adNetwork;
    userProperties.lastEntryAdType = entryData.adType;
    userProperties.lastEntryAdIncentive = entryData.adIncentive;
    userProperties.lastEntryAdMedia = entryData.adMedia;
    userProperties.lastEntryAdDuration = entryData.adDuration;
    userProperties.lastEntryAdUnit = entryData.adUnit;
    userProperties.lastEntryAdLanguage = entryData.adLanguage;
    userProperties.lastEntryAdCountry = entryData.adCountry;
    userProperties.lastEntryAdCampaign = entryData.adCampaign;
    userProperties.lastEntryAdSet = entryData.adSet;
    userProperties.lastEntryAbTest = entryData.abTest;
    userProperties.lastEntryAbTestBucket = entryData.abTestBucket;
    userProperties.lastEntryChannelIsInferred = !this.entryData.$channel;
    userProperties.lastEntryChannelIsSocial = isSocialChannel(
      this.getChannel(),
    );

    analytics.setUserProperties(userProperties, analytics.toDefaultAndAdServer);
  }

  protected _setCanSubscribeToBot(value: boolean): void {
    analytics.setUserProperties({ canSubscribeBot: value });
    this.canSubscribeToBot = value;
  }

  protected _setContextSquad(squad: Squad | null): void {
    analytics.setUserProperties({ contextIsSquad: !!squad });
    this.contextSquad = squad;
  }

  protected _setContextTournament(contextTournament: Tournament | null): void {
    analytics.setUserProperties({ contextIsTournament: !!contextTournament });
    this.contextTournament = contextTournament;
  }

  protected _setIsSubscribedToBot(value: boolean): void {
    analytics.setUserProperties({ isSubscribed: value });
    this.isSubscribedToBot = value;
  }

  protected _updateContext(
    contextAPI: AnalyticsProperties.ContextAPIType,
    context: { getID: () => string; getType: () => string },
  ): void {
    // Context switching always resets the current tournament and squad:
    this._setContextTournament(null);
    this._setContextSquad(null);

    this.contextID = context.getID();
    this.contextType = context.getType(); // TODO: make values consistent between platforms (POST|THREAD|GROUP|SOLO)

    // Only update contextAPI on launch if we have a context ID, otherwise we end up with
    // `contextAPI: Launch, contextID: <stale context ID from previous session>` in Amplitude:
    const updateContextAPI = contextAPI !== 'Launch' || this.contextID;

    analytics.setUserProperties(
      {
        contextID: this.contextID,
        contextType: this.contextType,
        ...(updateContextAPI ? { contextAPI } : {}),
      },
      analytics.toDefaultAndAdServer,
    );

    if (contextAPI !== 'Launch' && contextAPI !== 'GetTournamentAsync') {
      this.afterContextSwitchCallback?.();
    }
  }

  _populateEntryDataEventProperties(
    eventProperties: Record<string, any>,
  ): void {
    const entryData = this.entryData;

    // Add referrer's A/B test buckets to Entry/EntryFinal event properties.
    // User properties are not used because we currently don't delete user properties
    // on each entry, so subsequent entries from non-viral sources will contain the
    // stale sourceAB user properties from the previous viral entry.
    for (const entryProp in entryData) {
      if (entryProp.startsWith('sourceAB_')) {
        eventProperties[entryProp] = entryData[entryProp];
      }
    }

    eventProperties.$sourceFirstEntryVersion = entryData.$firstEntryVersion;
    eventProperties.$wasEntryDataReadyEarly = this.wasEntryDataReadyEarly;
    eventProperties.absoluteSequence = entryData.absoluteSequence;
    eventProperties.isAppSwitch = entryData.isAppSwitch;
    eventProperties.sessionSequence = entryData.sessionSequence;
    eventProperties.featureSessionSequence = entryData.featureSessionSequence;
    eventProperties.featureAbsoluteSequence = entryData.featureAbsoluteSequence;
    eventProperties.sourceABTag = entryData.ABTag;
    eventProperties.sourceDate = entryData.date;
    eventProperties.sourceDateHour = entryData.dateHour;
    eventProperties.sourceDateTime = entryData.dateTime;
    eventProperties.sourceCountry = entryData.country;
    eventProperties.sourceHour = entryData.hour;
    eventProperties.sourceSelectionUI = entryData.selectionUI;
    eventProperties.sourceShareDestination = entryData.destination;
    eventProperties.sourceTournamentAPI = entryData.tournamentAPI;
    eventProperties.sourceTracerKey = entryData.tracerKey;
    eventProperties.sourceUpdatePushRequested = entryData.pushRequested;
    eventProperties.sourceUpdateStrategy = entryData.strategy;
    eventProperties.sourceUpdateTemplateID = entryData.templateID;
    eventProperties.tournamentPayloadMode = this.tournamentPayloadMode;

    if (this.platformID === 'line-guest') {
      eventProperties.isGuest = true;
    }

    // TODO: invesitage if following properties really necessary
    // (user properties can be used as substitutes)
    eventProperties.adNetwork = entryData.adNetwork;
    eventProperties.adCampaign = entryData.adCampaign;
    eventProperties.adSet = entryData.adSet;
    eventProperties.abTest = entryData.abTest;
    eventProperties.abTestBucket = entryData.abTestBucket;
    eventProperties.adType = entryData.adType;
    eventProperties.adID = entryData.adID;
    eventProperties.adIncentive = entryData.adIncentive;
    eventProperties.adMedia = entryData.adMedia;
    eventProperties.adDuration = entryData.adDuration;
    eventProperties.adUnit = entryData.adUnit;
    eventProperties.adLanguage = entryData.adLanguage;
    eventProperties.adCountry = entryData.adCountry;
    eventProperties.channelIsSocial = isSocialChannel(this.getChannel());
    eventProperties.sourceDaysSinceInstall = entryData.daysSinceInstall;
    eventProperties.sourceGameFeature = entryData.gameFeature;
    eventProperties.sourceGameID = entryData.gameID;
    eventProperties.sourceGamePlayerID = entryData.gamePlayerID;
    eventProperties.sourceGameVersion = entryData.gameVersion;
    eventProperties.sourcePlayerID = entryData.playerID;
    eventProperties.sourceVersion = entryData.sourceVersion;
    eventProperties.sourceLevel = entryData.sourceLevel;
    eventProperties.sourceLevelRange = entryData.sourceLevelRange;
    eventProperties.sourceFeature = entryData.feature;
    eventProperties.sourceSubFeature = entryData.$subFeature;
    eventProperties.$subFeature = entryData.$subFeature; // For backwards compatibility [AN-15].
    eventProperties.UID = entryData.UID;
    eventProperties.asset = entryData.asset;
    eventProperties.text = entryData.text;
    eventProperties.button = entryData.button;

    // Payloads created before this is added to specific platform won't include receivers at all.
    // Currently only set on LINE.
    if (entryData.receivers) {
      eventProperties.sourceUpdateIsToPlayer = entryData.receivers.find(
        (receiver) => receiver.id === this.playerID,
      )?.isPlayer;
    }
  }

  /**
   * Resolves after entry analytics have been sent
   */
  waitForEntryAnalytics(): Promise<void> {
    return this._entryAnalyticsPromise;
  }

  private _sendEntryAnalytics(): Promise<void> {
    const resolveEntryAnalyticsPromise = this._resolveEntryAnalytics;
    if (!resolveEntryAnalyticsPromise) {
      throw Error('Entry analytics have already been sent');
    }
    /**
     * We set this to null and test on its existence to validate and
     * sanity check that _sendEntryAnalytics can only be evaluated once.
     */
    this._resolveEntryAnalytics = null;

    Promise.all([
      this._createEntryPromise(),
      this.loadStorage().then(() => void this.trackTimeTo('loadStorage')),
    ])
      .catch((error) => {
        analytics.pushError('EntryError', error);
        return Promise.resolve();
      })
      .then(() => {
        const entryCount = this.storage.entry.count;
        const now = globalTime.getNow();
        const date = new Date(now).toISOString().slice(0, 10).replace(/-/g, ''); // `date` uses UTC instead of `amplitudeTimeZone`

        const lastEntryTime = this.storage.entry.last;
        const lastEntryDay = new Date(lastEntryTime);
        const today = new Date(now);
        const isFirstEntryOfDay =
          today.getDate() !== lastEntryDay.getDate() ||
          today.getMonth() !== lastEntryDay.getMonth() ||
          today.getFullYear() !== lastEntryDay.getFullYear();

        const { dateHour, dateTime, hour } = getAnalyticsTimeProperties(
          now,
          this._gameConfig.amplitudeTimeZone!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        );

        const sharedUserProps: Partial<AnalyticsProperties.InternalUserProperties> =
          {
            contextID: this.contextID,
            contextType: this.contextType,
            contextIsSquad: !!this.contextSquad,
            contextIsTournament: !!this.contextTournament,
            entryCount: entryCount,
            adFeatureAvailable:
              this.canShowInterstitialAds || this.canShowRewardedVideoAds,
            lastEntryIsFirstOfDay: isFirstEntryOfDay,
            lastEntryPaymentsEnabled: this.paymentsAvailable,
            lastEntryVersion: this._gameConfig.version,
            lastEntryDate: date,
            lastEntryDateHour: dateHour,
            lastEntryDateTime: dateTime,
            lastEntryHour: hour,
          };

        this._setEntryDataUserProperties(sharedUserProps);

        const entryEventsBundle: any = {
          first: entryCount === 1,
          date,
          dateHour,
          dateTime,
          connectedPlayerCount: this.friends.length,
          hour,
          entryPointName: this.entryPointName,
          isFirstEntryOfDay,

          // TODO: remove (deprecated, is now a user property)
          adFeatureAvailable:
            this.canShowInterstitialAds || this.canShowRewardedVideoAds,
        };

        this._populateEntryDataEventProperties(entryEventsBundle);

        analytics.pushEvent(
          'Entry',
          entryEventsBundle,
          analytics.toDefaultAndAdServer,
        );

        this.entryAnalyticsBundle = entryEventsBundle;
      })
      .then(resolveEntryAnalyticsPromise.resolve)
      .catch((err) => {
        resolveEntryAnalyticsPromise.reject(err);
        throw err;
      });

    return this._entryAnalyticsPromise;
  }

  /**
   * Tracks the first button tap. Call this when a button is clicked.
   *
   * @param analyticsData Any custom analytics the game wants to track.
   */
  trackFirstButtonTap(analyticsData: CommonAnalytics): void {
    if (this.didTrackFirstButtonTap) {
      // Let the game call this from multiple buttons, but have it do nothing.
      return;
    }

    this.didTrackFirstButtonTap = true;

    events.trackFirstButtonTap({ occasion: 'sessionStart', ...analyticsData });
  }

  /**
   * Tracks a [`Pinch`](https://docs.dev.gc-internal.net/gcinstant/events-reference/index.html#pinch) event.
   */
  trackPinch(analyticsData: {
    /** The pinched resource. Examples: "spins", "coins", "gems", etc. */
    resource: string;
    /** Game feature that triggered the pinch. Examples: "singlespin", "autospin", "launch", "nav", etc. */
    trigger: string;

    /** Any other custom data the user wants to pass. */
    [key: string]: any;
  }): void {
    this.trackPinchSessionCount = (this.trackPinchSessionCount || 0) + 1;
    this.trackPinchSessionCounts[analyticsData.resource] =
      (this.trackPinchSessionCounts[analyticsData.resource] || 0) + 1;

    this.storage.trackPinchAllTimeCount =
      (this.storage.trackPinchAllTimeCount || 0) + 1;
    this.storage.trackPinchAllTimeCounts[analyticsData.resource] =
      (this.storage.trackPinchAllTimeCounts[analyticsData.resource] || 0) + 1;

    events.trackPinch({
      allTimeCount: this.storage.trackPinchAllTimeCount,
      allTimeCountForResource:
        this.storage.trackPinchAllTimeCounts[analyticsData.resource],
      sessionCount: this.trackPinchSessionCount,
      sessionCountForResource:
        this.trackPinchSessionCounts[analyticsData.resource],

      ...analyticsData,
    });
  }

  /**
   * Set entry user properties and send the canonical `EntryFinal` analytics event.
   * Call this after finishing all initialization and starting the game.
   *
   * GCInstant sets a number of event and user properties internally; use the
   * parameters to pass in additional game-specific properties.
   *
   * @throws if called more than once.
   *
   * @param entryFinalEventProperties Extra event properties of the `EntryFinal` event.
   * @param firstEntryUserProperties Extra user properties set for new users.
   * @param lastEntryUserProperties Extra user properties set for all users.
   */
  sendEntryFinalAnalytics(
    entryFinalEventProperties: { [key: string]: any },
    firstEntryUserProperties: { [key: string]: any },
    lastEntryUserProperties: { [key: string]: any },
  ): Promise<void> {
    this.throwIfSendEntryFinalAnalyticsIsCalledMultipleTimes();

    return this.waitForEntryAnalytics().then(() => {
      const entryData = this.entryData;
      const entryEventsBundle = this.entryAnalyticsBundle;

      // CORE-448: Recalculate entry data user and event properties to account
      // for entry properties added after _sendEntryAnalytics, as in case of
      // tournament inference
      this._setEntryDataUserProperties();
      this._populateEntryDataEventProperties(entryEventsBundle);

      const $channel = this.getChannel();
      const $channelIsInferred = !this.entryData.$channel;
      const $platform = this.osType;

      // AN-8: Infer FB/IG ad network names from campaign name prefix
      let $adNetworkName = entryData.$adNetworkName;
      if (!entryData.$adNetworkName) {
        if (entryData.$adCampaignName?.startsWith('FB ')) {
          $adNetworkName = 'Facebook';
        } else if (entryData.$adCampaignName?.startsWith('IG ')) {
          $adNetworkName = 'Instagram';
        }
      }

      const now = globalTime.getNow();
      const isFirstEntry = entryEventsBundle.first;
      if (isFirstEntry) {
        const generationProperties = getNextGenerationProperties(
          { ...this.entryData, $adNetworkName }, // Avoid mutating this.entryData.$adNetworkName
          now,
          { channel: $channel, playerID: this.playerID },
        );
        const lastProps = analytics.getUserProperties();

        const firstEntryProps: AnalyticsProperties.FirstEntryUserProperties = {
          ...firstEntryUserProperties,
          ...generationProperties,

          $firstEntryChannel: $channel,
          $firstEntryPlatform: $platform,
          $firstEntrySourceLastEntryPlatform: entryData.$lastEntryPlatform,
          $firstEntrySourceLastEntrySessionMaturityRange:
            entryData.$lastEntrySessionMaturityRange,

          firstEntryActiveClusterSize1D: lastProps.lastEntryActiveClusterSize1D,
          firstEntryActiveClusterSize3D: lastProps.lastEntryActiveClusterSize3D,
          firstEntryActiveClusterSize7D: lastProps.lastEntryActiveClusterSize7D,
          firstEntryActiveClusterSize14D:
            lastProps.lastEntryActiveClusterSize14D,
          firstEntryActiveClusterSize30D:
            lastProps.lastEntryActiveClusterSize30D,
          firstEntryActiveClusterSize90D:
            lastProps.lastEntryActiveClusterSize90D,
          firstEntryActiveClusterConnections1D:
            lastProps.lastEntryActiveClusterConnections1D,
          firstEntryActiveClusterConnections3D:
            lastProps.lastEntryActiveClusterConnections3D,
          firstEntryActiveClusterConnections7D:
            lastProps.lastEntryActiveClusterConnections7D,
          firstEntryActiveClusterConnections14D:
            lastProps.lastEntryActiveClusterConnections14D,
          firstEntryActiveClusterConnections30D:
            lastProps.lastEntryActiveClusterConnections30D,
          firstEntryActiveClusterConnections90D:
            lastProps.lastEntryActiveClusterConnections90D,
          firstEntryActiveClusterConnectedness1D:
            lastProps.lastEntryActiveClusterConnectedness1D,
          firstEntryActiveClusterConnectedness3D:
            lastProps.lastEntryActiveClusterConnectedness3D,
          firstEntryActiveClusterConnectedness7D:
            lastProps.lastEntryActiveClusterConnectedness7D,
          firstEntryActiveClusterConnectedness14D:
            lastProps.lastEntryActiveClusterConnectedness14D,
          firstEntryActiveClusterConnectedness30D:
            lastProps.lastEntryActiveClusterConnectedness30D,
          firstEntryActiveClusterConnectedness90D:
            lastProps.lastEntryActiveClusterConnectedness90D,

          firstEntryActiveFriendCount1D: lastProps.lastEntryActiveFriendCount1D,
          firstEntryActiveFriendCount3D: lastProps.lastEntryActiveFriendCount3D,
          firstEntryActiveFriendCount7D: lastProps.lastEntryActiveFriendCount7D,
          firstEntryActiveFriendCount14D:
            lastProps.lastEntryActiveFriendCount14D,
          firstEntryActiveFriendCount30D:
            lastProps.lastEntryActiveFriendCount30D,
          firstEntryActiveFriendCount90D:
            lastProps.lastEntryActiveFriendCount90D,
          'firstEntryActiveFriendRatio1/3':
            lastProps['lastEntryActiveFriendRatio1/3'],
          'firstEntryActiveFriendRatio1/7':
            lastProps['lastEntryActiveFriendRatio1/7'],
          'firstEntryActiveFriendRatio3/7':
            lastProps['lastEntryActiveFriendRatio3/7'],
          'firstEntryActiveFriendRatio7/30':
            lastProps['lastEntryActiveFriendRatio7/30'],
          firstEntrySourceRealtimeActiveFriendCount1D:
            entryData.realtimeActiveFriendCount1D,
          firstEntrySourceRealtimeActiveFriendCount3D:
            entryData.realtimeActiveFriendCount3D,
          firstEntrySourceRealtimeActiveFriendCount7D:
            entryData.realtimeActiveFriendCount7D,
          firstEntrySourceRealtimeActiveFriendCount14D:
            entryData.realtimeActiveFriendCount14D,
          firstEntrySourceRealtimeActiveFriendCount30D:
            entryData.realtimeActiveFriendCount30D,
          firstEntrySourceRealtimeActiveFriendCount90D:
            entryData.realtimeActiveFriendCount90D,

          firstEntryConnectedPlayerCount:
            entryEventsBundle.connectedPlayerCount,
          firstEntryContextID: entryData.contextID,
          firstEntryAdNetwork: entryData.adNetwork,
          firstEntryAdCampaign: entryData.adCampaign,
          firstEntryAdSet: entryData.adSet,
          firstEntryAbTest: entryData.abTest,
          firstEntryAbTestBucket: entryData.abTestBucket,
          firstEntryAdType: entryData.adType,
          firstEntryAdIncentive: entryData.adIncentive,
          firstEntryAdMedia: entryData.adMedia,
          firstEntryAdDuration: entryData.adDuration,
          firstEntryAdUnit: entryData.adUnit,
          firstEntryAdLanguage: entryData.adLanguage,
          firstEntryAdCountry: entryData.adCountry,
          firstEntryChannelIsInferred: $channelIsInferred,
          firstEntryChannelIsSocial: isSocialChannel(this.getChannel()),
          firstEntryDate: entryEventsBundle.date,
          firstEntryDateHour: lastProps.lastEntryDateHour,
          firstEntryDateTime: lastProps.lastEntryDateTime,
          firstEntryHour: lastProps.lastEntryHour,
          firstEntrySourceFeature: entryData.feature,
          firstEntrySourceGameFeature: entryData.sourceGameFeature,
          firstEntrySourceGameID: entryData.gameID,
          firstEntrySourceGamePlayerID: entryData.gamePlayerID,
          firstEntrySourcePlayerID: entryData.playerID,
          firstEntrySourceGameVersion: entryData.gameVersion,
          firstEntrySourceGameLevel: entryData.gameLevel,
          firstEntrySourceLevel: entryData.sourceLevel,
          firstEntrySourceLevelRange: entryData.sourceLevelRange,
          firstEntrySourceOS: entryData.sourceOS,
          firstEntrySourceVersion: entryData.sourceVersion,
          firstEntryUID: entryData.UID,
          firstEntryAsset: entryData.asset,
          firstEntryText: entryData.text,
          firstEntryButton: entryData.button,
          firstEntryVersion: this._gameConfig.version,
          firstEntryPaymentsEnabled: this.paymentsAvailable,
          firstEntryPointName: entryEventsBundle.entryPointName,
        };

        // native version
        if (this.osType === 'IOS_APP' || this.osType === 'ANDROID_APP') {
          const version = window.Native?.app?.version;
          if (version) {
            const name =
              this.osType === 'IOS_APP'
                ? 'firstEntryAppleVersion'
                : 'firstEntryAndroidVersion';
            firstEntryProps[name] = version;
          }
        }

        // AB tests are initialized by the game, so it's possible they haven't been initialized yet.
        // If a game isn't running AB tests (yet), they may not be initialized at all.
        if (this.abTests?.config) {
          firstEntryProps.firstEntryABTag = this.abTests.getAbTag();
        }

        if (this.platformID === 'line') {
          firstEntryProps.firstEntrySocialFriendCount =
            this.socialFriends.length;
        }

        analytics.setUserProperties(
          firstEntryProps,
          analytics.toDefaultAndAdServer,
        );

        // Save first entry user props to storage
        for (const key in firstEntryProps) {
          this.storage.analytics[key] = firstEntryProps[key];
        }
      }

      const entry = this.storage.entry;
      const lastEntryTime = entry.last;
      let lastNativeEntry = entry.lastNativeEntry;

      entry.last = now;
      if (entry.count === 1) {
        entry.first = now;
      }
      if ($channel === 'AD') {
        entry.lastAdEntry = now;
      }
      // applePushEnabled only comes from the native app
      if (entryData.applePushEnabled !== undefined) {
        entry.lastNativeEntry = now;
        lastNativeEntry = lastNativeEntry || now;
      }

      const daysElapsedFirstEntry = (now - entry.first) / dayInMs;
      const hoursInactive =
        lastEntryTime === 0 ? 0 : (now - lastEntryTime) / 1000 / 60 / 60;

      const { $zeroEntryDatetime } = this.storage
        .analytics as AnalyticsProperties.FirstEntryUserProperties;
      const $lastEntryElapsedDaysZeroEntry =
        $zeroEntryDatetime != null
          ? (now - new Date($zeroEntryDatetime).getTime()) / dayInMs
          : null;

      // CORE-607 Track whether user entered through the same link as they did in previous session:
      const currentPayload = entryDataToPayloadIdentifierObject(
        this.entryData,
        this.entryPointName,
      );
      const lastEntryIsRepeat =
        hoursInactive <= 1 &&
        !!this.storage.previousPayload &&
        JSON.stringify(currentPayload, Object.keys(currentPayload).sort()) ===
          JSON.stringify(
            this.storage.previousPayload,
            Object.keys(this.storage.previousPayload).sort(),
          );
      this.storage.previousPayload = currentPayload;

      const referralChainLengths = getReferralChainLengths(
        entryData.sourceChainTimestamps || [],
        now,
      );

      const lastEntryChainLengths = Object.entries(referralChainLengths).reduce(
        (acc, [key, val]) => ({ ...acc, [`lastEntryChainLength${key}`]: val }),
        {} as AnalyticsProperties.LastEntryChainLengthProperties,
      );

      const lastEntryProps: Omit<
        AnalyticsProperties.LastEntryUserProperties,
        'lastEntryChannelIsInferred' | 'lastEntryChannelIsSocial' // These are already set in `_setEntryDataUserProperties`
      > = {
        ...lastEntryUserProperties,
        ...lastEntryChainLengths,
        $lastEntryChannel: $channel,
        $lastEntryPlatform: $platform,
        $lastEntrySessionMaturityRange: getSessionMaturityRange(entry.count),
        $lastEntrySourceLastEntryPlatform: entryData.$lastEntryPlatform,
        $lastEntrySourceLastEntrySessionMaturityRange:
          entryData.$lastEntrySessionMaturityRange,

        $lastEntryAdCampaignName: entryData.$adCampaignName,
        $lastEntryAdSetName: entryData.$adSetName,
        $lastEntryAdCreativeName: entryData.$adCreativeName,
        $lastEntryAdNetworkName: $adNetworkName,

        $lastEntryElapsedDaysZeroEntry,

        requestedLocale: this.locale,
        platformLocale: this.platformLocale,
        displayedLocale: this.locale,
        lastConnectedPlayerCount: entryEventsBundle.connectedPlayerCount,
        lastEntryIsRepeat,
        lastEntryPointName: entryEventsBundle.entryPointName,
        daysSinceInstall: Math.floor(daysElapsedFirstEntry),
        daysSinceLastAdEntry: entry.lastAdEntry
          ? Math.floor((now - entry.lastAdEntry) / dayInMs)
          : undefined,
        daysElapsedFirstEntry: daysElapsedFirstEntry,
        daysElapsedLastEntry: hoursInactive / 24,

        lastEntrySourceRealtimeActiveFriendCount1D:
          entryData.realtimeActiveFriendCount1D,
        lastEntrySourceRealtimeActiveFriendCount3D:
          entryData.realtimeActiveFriendCount3D,
        lastEntrySourceRealtimeActiveFriendCount7D:
          entryData.realtimeActiveFriendCount7D,
        lastEntrySourceRealtimeActiveFriendCount14D:
          entryData.realtimeActiveFriendCount14D,
        lastEntrySourceRealtimeActiveFriendCount30D:
          entryData.realtimeActiveFriendCount30D,
        lastEntrySourceRealtimeActiveFriendCount90D:
          entryData.realtimeActiveFriendCount90D,
      };

      if (this.platformID === 'line') {
        lastEntryProps.lastEntrySocialFriendCount = this.socialFriends.length;
      }

      analytics.setUserProperties(
        lastEntryProps,
        analytics.toDefaultAndAdServer,
      );

      // EntryFinal

      const daysInactive = hoursInactive / 24;

      // Avoid mutating incoming object
      const entryFinalEventProps = {
        ...entryEventsBundle,
        ...entryFinalEventProperties,
        inactiveDay5: daysInactive > 5,
        inactiveDay10: daysInactive > 10,
        inactiveDay30: daysInactive > 30,
        inactiveDay90: daysInactive > 90,
        sourceShareSwitchContext: entryData.switchContext,
        $channel,
        $channelIsInferred,
        $self: this.entryData.playerID === this.playerID,
        $adCampaignName: entryData.$adCampaignName,
        $adSetName: entryData.$adSetName,
        $adCreativeName: entryData.$adCreativeName,
        $adNetworkName,
        $wasEntryDataFullyReadyEarly:
          this.wasEntryDataReadyEarly && this.onGameStartRetries.length === 0,

        // First session user properties from the referrer
        $sourceFirstEntryChannel: entryData.$firstEntryChannel,
        $sourceFirstConnectedPlayerCount: entryData.$firstConnectedPlayerCount,
        $sourceFirstEntryDate: entryData.$firstEntryDate,
        $sourceFirstEntryPointName: entryData.$firstEntryPointName,

        // Last session user properties from the referrer
        $sourceLastEntryChannel: entryData.$lastEntryChannel,
        $sourceEntryCount: entryData.$entryCount,
        $sourceLastConnectedPlayerCount: entryData.$lastConnectedPlayerCount,
        $sourceLastEntryPointName: entryData.$lastEntryPointName,
        $sourceLastEntryVersion: entryData.$lastEntryVersion,

        sourceLastEntryActiveClusterSize1D:
          entryData.lastEntryActiveClusterSize1D,
        sourceLastEntryActiveClusterSize3D:
          entryData.lastEntryActiveClusterSize3D,
        sourceLastEntryActiveClusterSize7D:
          entryData.lastEntryActiveClusterSize7D,
        sourceLastEntryActiveClusterSize14D:
          entryData.lastEntryActiveClusterSize14D,
        sourceLastEntryActiveClusterSize30D:
          entryData.lastEntryActiveClusterSize30D,
        sourceLastEntryActiveClusterSize90D:
          entryData.lastEntryActiveClusterSize90D,
        sourceLastEntryActiveClusterConnections1D:
          entryData.lastEntryActiveClusterConnections1D,
        sourceLastEntryActiveClusterConnections3D:
          entryData.lastEntryActiveClusterConnections3D,
        sourceLastEntryActiveClusterConnections7D:
          entryData.lastEntryActiveClusterConnections7D,
        sourceLastEntryActiveClusterConnections14D:
          entryData.lastEntryActiveClusterConnections14D,
        sourceLastEntryActiveClusterConnections30D:
          entryData.lastEntryActiveClusterConnections30D,
        sourceLastEntryActiveClusterConnections90D:
          entryData.lastEntryActiveClusterConnections90D,
        sourceLastEntryActiveClusterConnectedness1D:
          entryData.lastEntryActiveClusterConnectedness1D,
        sourceLastEntryActiveClusterConnectedness3D:
          entryData.lastEntryActiveClusterConnectedness3D,
        sourceLastEntryActiveClusterConnectedness7D:
          entryData.lastEntryActiveClusterConnectedness7D,
        sourceLastEntryActiveClusterConnectedness14D:
          entryData.lastEntryActiveClusterConnectedness14D,
        sourceLastEntryActiveClusterConnectedness30D:
          entryData.lastEntryActiveClusterConnectedness30D,
        sourceLastEntryActiveClusterConnectedness90D:
          entryData.lastEntryActiveClusterConnectedness90D,

        hoursInactive: Math.floor(hoursInactive),
        daysInactive: Math.floor(daysInactive),

        applePushEnabled: entryData.applePushEnabled,
        appleDaysElapsedLastEntry:
          lastNativeEntry && (now - lastNativeEntry) / dayInMs,
      };

      let timestamp = entryData.timestamp;
      if (timestamp) {
        // TODO: deprecate string timestamps
        if (typeof timestamp === 'string') {
          // Extra code due to legacy notifications
          // where payload timestamp is under ISO string format
          timestamp = Date.parse(timestamp);
          entryData.timestamp = timestamp;
        }

        const elapsedSecs = (now - timestamp) / 1000;
        entryFinalEventProps.elapsedMinutes = elapsedSecs / 60;
        entryFinalEventProps.elapsedHours = elapsedSecs / 60 / 60;
        entryFinalEventProps.elapsedDays = elapsedSecs / 60 / 60 / 24;
      }

      // Include entry sequence async API call timings in event properties:
      for (const key in this.timeTo) {
        const propertyName =
          'timeTo' + key.charAt(0).toUpperCase() + key.slice(1);
        entryFinalEventProps[propertyName] = this.timeTo[key];
      }

      // Include referral chain analytics in event properties:
      for (const [key, val] of Object.entries(referralChainLengths)) {
        if (val !== '(none)') {
          entryFinalEventProps[`chainLength${key}`] = val;
        }
      }

      analytics.pushEvent(
        'EntryFinal',
        entryFinalEventProps,
        this._gameConfig.fbPixelId
          ? analytics.toDefaultAndPixelAndAdServer
          : analytics.toDefaultAndAdServer,
      );

      this.sendConversionEvents({
        immediateReferrerId: entryData.playerID,
        zeroGenerationReferrerId: entryData.zeroEntrySourcePlayerID,
        eventProperties: entryFinalEventProps,
      });

      if (this._gameConfig.postbackManagerKey) {
        void this.fireMarketingPostback(); // Error is tracked in `fireMarketingPostback`
      }

      // TODO: EntryContextOriginator
      // When a player sends an update to a new context (a context that has never played our game), we cache EntryFinal on our server
      // If any player installs from a context, we look up that context’s cached EntryFinal and send it as EntryContextOriginator
      // analytics.pushEvent('EntryContextOriginator', entryContextBundle);
    });
  }

  private async fireMarketingPostback() {
    if (!this.entryData.$adNetworkName) {
      return;
    }
    const postbackManager = new PostbackManager(
      this._gameConfig.postbackManagerKey,
    );
    let os: 'android' | 'ios';
    switch (this.osType) {
      case 'ANDROID':
        os = 'android';
        break;
      case 'IOS':
        os = 'ios';
        break;
      case 'MOBILE_WEB':
        // In mobile web, we can be either on iOS or Android. Detect whether it's mobile safari and decide.
        os =
          navigator.userAgent.match(/(iPod|iPhone|iPad)/) &&
          navigator.userAgent.match(/AppleWebKit/)
            ? 'ios'
            : 'android';
        break;

      default:
        // Attribute everything else to android
        os = 'android';
        break;
    }

    // map country name back to 2 digit code
    const meta: { os: 'ios' | 'android'; country?: string } = { os };
    if (this.countryName) {
      meta.country = getIsoCodeFromCountryName(this.countryName);
    }

    try {
      await postbackManager.sendEvent(
        PostbackManagerEvents.AppLaunch,
        this.entryData,
        meta,
      );
    } catch (e) {
      // just swallow the error, we don't want this to be fatal.
      events.trackMarketingPostbackFailure(e, {
        $adNetworkName: this.entryData.$adNetworkName,
      });
    }
  }

  private sendConversionEvents(opts: {
    immediateReferrerId?: string;
    zeroGenerationReferrerId?: string;
    eventProperties: { [key: string]: any };
  }): void {
    if (!opts.immediateReferrerId && !opts.zeroGenerationReferrerId) {
      return;
    }

    const events = [
      // Send an EntryConversion event to referrer's event stream:
      {
        userID: opts.immediateReferrerId,
        eventName: 'EntryConversion',
        eventProperties: opts.eventProperties,
      },

      // Send an ZeroEntryConversion event to zero generation user's event stream:
      {
        userID: opts.zeroGenerationReferrerId,
        eventName: 'ZeroEntryConversion',
        eventProperties: opts.eventProperties,
      },
    ].filter(
      // Ignore events without a target user.
      ({ userID }) => !!userID,
    );

    const userIds = events
      .map(({ userID }) => userID)
      .filter((x, i, arr) => arr.indexOf(x) === i);

    void this.getTemporalUserProperties(userIds).then((temporalUserProps) => {
      events.forEach(
        (event) =>
          this.sendRawAnalyticsEvent({
            ...event,
            userProperties: temporalUserProps[event.userID] || {},
          }).catch(() => undefined), // Error is tracked in `sendRawAnalyticsEvent`
      );
    });
  }

  /**
   * Send a raw analytics event through Amplitude's HTTP API. Useful for sending
   * events to another user's event stream.
   *
   * @param event Analytics event sent as is without supplementing with contextual properties.
   * @throws In case of network error or non-2xx HTTP status code.
   */
  async sendRawAnalyticsEvent(event: {
    eventName: string;
    eventProperties: { [key: string]: any };
    userID: string;
    userProperties: { [key: string]: any };
  }): Promise<void> {
    const amplitudeEvent = {
      event_type: event.eventName,
      event_properties: event.eventProperties,
      user_id: event.userID,
      user_properties: event.userProperties,
    };

    if (!this._gameConfig.amplitudeKey) {
      if (this._gameConfig.isDevelopment) {
        console.log('rawAnalyticsEvent', amplitudeEvent);
      }
      return;
    }

    await retryWithTiming(async () => {
      const res = await fetch('https://api.amplitude.com/2/httpapi', {
        method: 'POST',
        body: JSON.stringify({
          api_key: this._gameConfig.amplitudeKey,
          events: [amplitudeEvent],
        }),
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        let responseBody: string | undefined;
        try {
          responseBody = await res.text();
        } catch {
          // swallow error
        }

        throw Error(
          `Request failed with status code ${res.status}: ${responseBody}`,
        );
      }
    }).catch(({ error, requestMs, requestTries }: TimedRetryError) => {
      events.trackSendRawAnalyticsEventFailure(error, {
        requestMs,
        requestTries,
      });

      throw error;
    });
  }

  logEvent(
    eventName: string,
    valueToSum?: number,
    parameters?: { [key: string]: string },
  ): void {
    console.log(
      'FBInstant.logEvent()',
      'eventName:',
      eventName,
      'valueToSum:',
      valueToSum,
      'parameters:',
      parameters,
    );
  }

  /** @returns entry channel, i.e., the channel through which player entered game. */
  private getChannel(): AnalyticsProperties.Channel {
    if (this.entryContextTournament) {
      return 'TOURNAMENT';
    }

    const entryPoint = this.entryPointName;

    let $channel = this.entryData.$channel;

    if (!$channel) {
      if (entryPoint === 'ad') {
        $channel = 'AD';
      } else if (entryPoint === 'bot_cta' || entryPoint === 'bot_menu') {
        $channel = 'CHATBOT';
      } else if (entryPoint === 'home_screen_shortcut') {
        $channel = 'HOMESCREEN';
      } else if (entryPoint === 'feed') {
        return 'SHARE';
      } else if (entryPoint === 'admin_message') {
        return 'UPDATE';
      } else {
        $channel = 'NONVIRAL';
      }
    }

    return $channel;
  }

  protected trackTimeTo(methodName: TimedMethod): void {
    this.timeTo[methodName] = Math.round(window.performance.now()) / 1000;
  }

  private throwIfNotInitialized(): void {
    if (!this._initialized) {
      throw Error(
        'Not initialized, game must resolve Platform.initializeAsync first',
      );
    }
  }

  private throwIfStartGameAsyncIsCalledMultipleTimes(): void {
    if (this.didCallStartGameAsync) {
      throw new Error(`Game should not call 'startGameAsync' more than once`);
    }

    this.didCallStartGameAsync = true;
  }

  /**
   * If a platform implementation doesn't use `Platform.sendEntryFinalAnalytics`,
   * it should call this at the start of its `sendEntryFinalAnalytics`.
   */
  private throwIfSendEntryFinalAnalyticsIsCalledMultipleTimes(): void {
    if (this.didCallSendEntryFinalAnalytics) {
      throw new Error(
        `Game should not call 'sendEntryFinalAnalytics' more than once`,
      );
    }

    this.didCallSendEntryFinalAnalytics = true;
  }

  private throwIfStorageAdapterIsNotConfigured(): void {
    if (!this.storage.hasAdapter() && !this._gameConfig.isDevelopment) {
      throw Error(
        'Storage adapter must be configured before loading storage in production mode',
      );
    }
  }

  /**
   * Create and initialize new native bridge using referral code flow.
   * Should be called before startGameAsync.
   */
  async createNativeBridge(
    baseURL: string,
    referralCode: string,
    secret: string,
    productIDs: string[] = [],
  ): Promise<void> {
    throw new Error('not implemented');
  }

  /**
   * Initialize an existing native bridge.
   * Should be called before startGameAsync.
   */
  async initializeNativeBridge(
    baseURL: string,
    bridgeSecret: string,
    productIds: string[] = [],
  ): Promise<void> {
    throw new Error('not implemented');
  }

  /**
   * Set a country code. A country name is derived from the code.
   * The country name is used for social events.
   *
   * @param countryCode A country code that consists of two upper-case letters.
   */
  setCountry(countryCode: string): void {
    this.countryName = getCountryNameFromIsoCode(countryCode) || undefined;
  }

  /**
   * Set the eCPM values used for tracking ad revenue.
   *
   * @param eCPM eCPM values in USD as returned by Replicant.
   *
   * @see https://docs.dev.gc-internal.net/gcinstant/ads/
   */
  setECPM(eCPM: ECPMOpts): void {
    this.eCPM = eCPM;
  }

  /**
   * Set exchange rates to be used for purchase analytics.
   *
   * @param exchangeRatesUSD A map of currency codes as keys, and USD values in those currencies as values.
   */
  setExchangeRatesUSD(
    exchangeRatesUSD: Partial<{ [currencyCode: string]: number }>,
  ): void {
    this.exchangeRatesUSD = exchangeRatesUSD;
  }

  /**
   * Set a source of external AB tests.
   *
   * AB test bucket assignments can be [viewed in analytics](https://docs.dev.gc-internal.net/gcinstant/ab-tests/index.html#viewing-in-analytics),
   * and the AB tag is appended to the existing [AB tag](https://docs.dev.gc-internal.net/gcinstant/ab-tests/index.html#ab-tag).
   *
   * Can be used to integrate [replicant AB tests](https://docs.dev.gc-internal.net/replicant/ab-tests/index.html).
   *
   * @param externalABTestsSource A source of external AB tests.
   */
  setExternalABTestsSource(externalABTestsSource: ExternalABTestsSource): void {
    this.externalABTests.setSource(externalABTestsSource);
  }

  protected tournamentWithPayloadDecoder(
    tournament: FBinstantTournament,
  ): Tournament {
    return {
      getContextID: () => tournament.getContextID().toString(),
      getEndTime: () => tournament.getEndTime(),
      getTitle: () => tournament.getTitle(),
      getPayload: async () => {
        const rawPayload = tournament.getPayload();
        if (!rawPayload) {
          return undefined;
        }

        let payloadObject: Record<string, any>;

        if (typeof rawPayload === 'string') {
          try {
            payloadObject = JSON.parse(rawPayload);
          } catch (error) {
            events.trackPayloadReadFailure(error, {
              rawPayload,
              type: 'parseFailed',
            });
            return undefined;
          }
        } else {
          payloadObject = rawPayload;
        }

        return this.dataCodec.decode(payloadObject);
      },
    };
  }

  /**
   * Returns true by default. Override the method in specific Platform to control incrementing entry count.
   */
  protected shouldIncreaseEntryCount(): boolean {
    return true;
  }
}

Platform.ERROR_NO_PROVISION =
  'Game must provide an onProvisionProductAsync callback';

export default Platform;
